import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  Checkbox,
  Snackbar,
  Portal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  HistoryOutlined as HistoryIcon,
  AdminPanelSettingsOutlined as ManagePermissionsIcon,
  EditOutlined as EditIcon,
  MoreVertOutlined as MoreVertIcon,
  LibraryAddOutlined as CloneIcon,
  ShareOutlined as ShareIcon,
  FavoriteBorderOutlined as LikeIcon,
  StarBorderOutlined as FavoriteIcon,
  Star as FavoritedIcon,
  LockOutlined as PrivateIcon,
  BusinessOutlined as CompanyIcon,
  GroupsOutlined as TeamIcon,
  PublicOutlined as CommunityIcon,
  TuneOutlined as CustomIcon,
  DeleteOutlineOutlined as DeleteIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
  VisibilityOutlined as PreviewIcon,
  ChatBubbleOutlined as ChatBubbleIcon,
  RestoreOutlined as RestoreIcon,
  FullscreenOutlined as FullscreenIcon,
  FullscreenExitOutlined as FullscreenExitIcon,
  ArrowBackOutlined as ArrowBackIcon,
  CodeOutlined as CodeIcon,
  InfoOutlined as InfoIcon,
  DownloadOutlined as DownloadIcon
} from '@mui/icons-material';
import { SaveOutlined as SaveIcon } from '@mui/icons-material';
import { useInteractions } from '../../../contexts/InteractionsContext';
import PromptExecuteChatPanel, { ExecuteHeaderBar } from './PromptExecuteChatPanel';
import {
  KnowledgeShelfToggleButton,
  executeChromeIconButtonInGroupSx,
} from './executeChromeUi';
import CapabilityInstructionBody from './CapabilityInstructionBody';
import VariableValueEditDialog from './VariableValueEditDialog';
import RuntimeInputsDialog from './RuntimeInputsDialog';
import PromptContextShelf from './PromptContextShelf';
import CreatorDocumentPane from './CreatorDocumentPane';
import CreatorRichEditor from './CreatorRichEditor';
import CreatorExecuteWorkspace from './CreatorExecuteWorkspace';
import KnowledgePreview from './KnowledgePreview';
import ContextBuilderDialog from '../context-builder/ContextBuilderDialog';
import ContextSelector from '../../prompt-builder/components/ContextSelector';
import VersionHistoryDialog from './VersionHistoryDialog';
import MarkdownPreviewEditor from '../MarkdownPreviewEditor';
import EditPreviewToggle from '../EditPreviewToggle';
import { capabilityDescriptionPreviewMarkdownSx, capabilityInstructionPreviewMarkdownSx, instructionBodyTextSx } from '../markdownBodySx';
import {
  expandedPromptShellPaperSx,
  expandedPromptShellTitleSx,
  capabilityInstructionsTitleSx,
  promptLibraryViewDialogPaperSx,
} from './expandedPromptShellPaperSx';
import CapabilityRunButton from './CapabilityRunButton';
import PermissionSettings from '../permissions/PermissionSettings';
import {
  createComment,
  getPromptComments,
  getContext,
  updateContext,
  exportDocx,
  updatePrompt,
  createContext,
  listCompanyUsers,
  listTeams
} from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { FAQ_LINKS, openFaq } from '../../../constants/faqLinks';
import { buildCapabilityMcpHandoff } from '../../../utils/capabilityMcpHandoff';
import { useSessionData } from '../../../contexts/SessionDataContext';
import useMobileKeyboardOpen from '../hooks/useMobileKeyboardOpen';
import { buildFullExecutionPayload } from '../../../utils/formatExecutionContext';
import {
  applyPromptSharingPreset,
  buildPermissionLevelUpdate,
  inferModalPermissionLevel,
  normalizePromptPermissions,
  resolveCanEditPromptPermissions
} from '../../../utils/promptPermissions';
import {
  buildPromptWithVariables,
  buildExecutionPromptWithVariables,
  mergeVariablesFromTemplate
} from '../../../utils/promptVariables';
import { getUserDisplayName, getUserInitials } from '../../../utils/userUtils';
import {
  promptCardFooterSx,
  promptCardHeaderSx,
  promptCardShellSx,
  promptContentGlowEmbeddedSx,
  promptContentGlowStandaloneSx,
} from '../../../constants/panelSurfaces';
import { formatUsagePlatformLabel } from '../../../constants/usageTargets';
import { resolveRecommendedModel } from '../../../constants/aiRouting';
import CapabilityTypeInfoChip from './CapabilityTypeInfoChip';
import Tooltip from '../LibraryTooltip';
import BuildQualityIndicator from './BuildQualityIndicator';
import { variableTokenChipInteractiveSx, variableTokenChipSx } from '../markdownBodySx';

const EDITABLE_PROMPT_SOURCE_EXTENSIONS = new Set(['', 'md', 'markdown', 'txt']);

const promptHasUsagePlatformMetadata = (prompt) => {
  if (!prompt) return false;
  return Boolean(
    (typeof prompt.whereToUse === 'string' && prompt.whereToUse.trim()) ||
    (typeof prompt.usagePlatform === 'string' && prompt.usagePlatform.trim()) ||
    (typeof prompt.usagePlatformOther === 'string' && prompt.usagePlatformOther.trim())
  );
};

const sanitizeExportPathSegment = (s, fallback = 'item') => {
  const t = String(s || fallback)
    .replace(/[\s\\/:*?"<>|]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^\.+|\.+$/g, '')
    .replace(/^-|-$/g, '');
  return t.slice(0, 80) || fallback;
};

const formatPermissionLevelLabel = (level) => {
  if (!level) return '';
  if (level === 'company') return 'Organization';
  if (level === 'group') return 'Custom';
  return `${level.charAt(0).toUpperCase()}${level.slice(1)}`;
};

// Helper function to render text with variables highlighted in blue and clickable
const renderTextWithHighlightedVariables = (text, variables = [], variableValues = {}, onVariableClick = null) => {
  if (!variables || variables.length === 0) {
    return text;
  }

  // Build a map of variable names to their variable objects
  const varObjectMap = new Map();
  variables.forEach(v => {
    varObjectMap.set(v.name, v);
  });

  // Build a map of variable names to their values (including placeholders)
  const varValueMap = new Map();
  variables.forEach(v => {
    const value = variableValues[v.name] !== undefined 
      ? (variableValues[v.name] || `{{${v.name}}}`)  // Use empty string as placeholder if explicitly set
      : (v.defaultValue || `{{${v.name}}}`);
    varValueMap.set(v.name, value);
  });

  // Find all matches: only {{variableName}} placeholders
  const matches = [];
  
  variables.forEach(v => {
    const varName = v.name;
    const value = varValueMap.get(varName);
    
    // Find all placeholder patterns {{variableName}}
    const placeholderPattern = new RegExp(`\\{\\{${varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`, 'g');
    let match;
    while ((match = placeholderPattern.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        varName,
        variable: varObjectMap.get(varName)
      });
    }
  });

  // Sort and merge overlapping matches (prioritize earlier matches)
  matches.sort((a, b) => a.start - b.start);
  
  // Remove overlapping matches (keep the first one)
  const filteredMatches = [];
  let lastEnd = -1;
  matches.forEach(match => {
    if (match.start >= lastEnd) {
      filteredMatches.push(match);
      lastEnd = match.end;
    }
  });

  // Build JSX elements
  const parts = [];
  let lastIndex = 0;
  let key = 0;

  filteredMatches.forEach(match => {
    // Add text before the match
    if (match.start > lastIndex) {
      parts.push(text.substring(lastIndex, match.start));
    }
    
    // Add the highlighted variable (clickable if onVariableClick is provided)
    const variable = match.variable;
    const displayValue = varValueMap.get(match.varName) ?? text.substring(match.start, match.end);
    
    parts.push(
      <Box
        key={key++}
        component="span"
        onClick={onVariableClick && variable ? (e) => {
          e.stopPropagation();
          onVariableClick(variable);
        } : undefined}
        sx={
          onVariableClick && variable
            ? variableTokenChipInteractiveSx
            : variableTokenChipSx
        }
      >
        {displayValue}
      </Box>
    );
    
    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 1 ? parts : text;
};

const DESCRIPTION_COLLAPSE_MAX_LINES = 3;

function descriptionNeedsCollapse(text) {
  if (!text) return false;
  const lineCount = text.split(/\r?\n/).filter((line) => line.trim()).length;
  return lineCount > 3 || text.length > 200;
}

const PromptDialog = ({
  open,
  onClose,
  prompt,
  closeLabel,
  variableValues,
  onVariableChange,
  constructedPrompt,
  onClone,
  onEdit,
  onTest,
  onComment,
  onDelete,
  onShare,
  onExport,
  onOpenBuilder,
  onFavorite,
  onRefresh,
  onPromptUpdated,
  onUsageIncrement: onUsageIncrementProp,
  showEditButton = true,
  showCommentButton = true,
  showCloneButton = true,
  showTestButton = true,
  showDeleteButton = false,
  showShareButton = true,
  customHeader = null,
  autoOpenExecutePanel = false,
  /** When autoOpenExecutePanel is true: if false, open execute UI without auto-sending the first run (human-in-the-loop project flows). */
  autoRunExecuteOnOpen = true,
  initialExecuteMode = 'chat',
  executePrompts = null,
  onExecutePromptSelect = null,
  executeSessionScopeKey = null,
  preserveExecuteSessionAcrossPrompts = false,
  initialIncludedContexts, // optional { [contextId]: true|false } e.g. from test modal to match builder selection
  /** When opening the dialog for a new prompt, expand the comments section (e.g. project capability card). */
  initialShowComments = false,
  userGroups: initialUserGroups = null,
  /** When set (discovery refine), restore-original uses this instead of displayed content. */
  onDiscoveryRevisionChange = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { interactions, version: interactionsVersion } = useInteractions();
  const { getGroups, groups: cachedGroups, getProfile, profile: cachedProfile } = useSessionData();
  
  const [userGroups, setUserGroups] = useState(initialUserGroups || []);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExecutePanel, setShowExecutePanel] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentTab, setCommentTab] = useState(0); // 0 = current version, 1 = all comments
  const [currentVersionComments, setCurrentVersionComments] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [expandedContexts, setExpandedContexts] = useState({});
  const [includedContexts, setIncludedContexts] = useState({}); // Track which contexts to include in copy
  const [copySuccessSnackbar, setCopySuccessSnackbar] = useState({ open: false, message: '' });
  const [localPrompt, setLocalPrompt] = useState(prompt); // Local state for prompt to enable real-time updates
  const savedRevisionRef = useRef(null);
  
  // Request access dialog state
  const [requestAccessContext, setRequestAccessContext] = useState(null);
  const [showRequestAccessDialog, setShowRequestAccessDialog] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [requestAccessSnackbar, setRequestAccessSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isPromptLocked, setIsPromptLocked] = useState(true); // Deprecated: prompt is always effectively "unlocked" in UI
  const [editedPromptText, setEditedPromptText] = useState(''); // Local state for edited prompt text
  const [originalPromptText, setOriginalPromptText] = useState(''); // Store original prompt text for comparison
  const [hasManualEdits, setHasManualEdits] = useState(false); // Track if user has manually edited the text
  const isLockingRef = useRef(false); // Track if we're in the process of locking (prevents race conditions)
  const hasManualEditsRef = useRef(false); // Ref version of hasManualEdits to prevent race conditions in effects
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [fullScreenEditedText, setFullScreenEditedText] = useState(''); // Edited text in full-screen
  const [fullScreenOriginalText, setFullScreenOriginalText] = useState(''); // Original text for full-screen
  const fullScreenInitializedRef = useRef(false); // Track if full-screen has been initialized

  const [variableMenuAnchor, setVariableMenuAnchor] = useState(null); // Anchor for variable menu
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null); // Anchor for top-right action menu
  const [permissionSubmenuAnchor, setPermissionSubmenuAnchor] = useState(null);
  const [editingVariable, setEditingVariable] = useState(null); // Currently editing variable
  const [variableMissingGateOpen, setVariableMissingGateOpen] = useState(false);
  const [variableMissingGateRows, setVariableMissingGateRows] = useState([]);
  const [variableMissingGateDraft, setVariableMissingGateDraft] = useState({});
  const previousPromptIdRef = useRef(null); // Track previous prompt ID to detect actual prompt changes
  const executeChatRef = useRef(null); // Ref to PromptExecuteChatPanel for execute() from Prompts section
  const executionPayloadOverrideRef = useRef(null);
  const variableGatePendingRef = useRef(null);
  const [executePanelExecuting, setExecutePanelExecuting] = useState(false); // Executing state for Prompts section button
  const [runExecuteOnOpen, setRunExecuteOnOpen] = useState(false); // When true, chat panel runs execute() once when mounted (after user clicked Execute to open)
  const [executeMode, setExecuteMode] = useState('chat'); // 'chat' | 'creator' for execute panel
  const [creatorDocumentContent, setCreatorDocumentContent] = useState(''); // Center pane in Creator mode
  const [executeSelectedModel, setExecuteSelectedModel] = useState(() => resolveRecommendedModel(prompt || {}));
  const [executeSessionByMode, setExecuteSessionByMode] = useState({ chat: null, creator: null });
  const [executeModelMenuAnchor, setExecuteModelMenuAnchor] = useState(null);
  const [temporaryContexts, setTemporaryContexts] = useState([]); // Session-only contexts for this modal
  const [showTemporaryContextBuilder, setShowTemporaryContextBuilder] = useState(false);
  const [showAttachExistingContextSelector, setShowAttachExistingContextSelector] = useState(false);
  const [editingAttachedContext, setEditingAttachedContext] = useState(null);
  const [temporaryContextBuilderUserProfile, setTemporaryContextBuilderUserProfile] = useState(null);
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const [mobileFooterActionsAnchor, setMobileFooterActionsAnchor] = useState(null);
  const [showMobileContextShelf, setShowMobileContextShelf] = useState(false);
  const mobileKeyboardOpen = useMobileKeyboardOpen(open && isMobile);
  const [creatorMobilePane, setCreatorMobilePane] = useState('document');
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [promptReadMode, setPromptReadMode] = useState('preview');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isSavingPermissionLevel, setIsSavingPermissionLevel] = useState(false);
  const [isPermissionDraftDirty, setIsPermissionDraftDirty] = useState(false);
  const [permissionDraftLevel, setPermissionDraftLevel] = useState('custom');
  const [permissionDraftPermissions, setPermissionDraftPermissions] = useState(() => normalizePromptPermissions(prompt?.permissions));
  const [permissionAccessList, setPermissionAccessList] = useState([]);
  const openCustomPermissionRef = useRef(null);
  const [showTempContextConvertDialog, setShowTempContextConvertDialog] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'save' | 'saveAs'
  const [pendingTempContexts, setPendingTempContexts] = useState([]);
  const [selectedTempContextIdsToConvert, setSelectedTempContextIdsToConvert] = useState([]);
  const [showConvertTempContextBuilder, setShowConvertTempContextBuilder] = useState(false);
  const [activeTempContextToConvert, setActiveTempContextToConvert] = useState(null);
  const [tempContextConversionQueue, setTempContextConversionQueue] = useState([]);
  const [convertedTempContextMap, setConvertedTempContextMap] = useState({});
  const [tempContextConversionStepKey, setTempContextConversionStepKey] = useState(0);
  const tempContextConversionQueueRef = useRef([]);
  const convertedTempContextMapRef = useRef({});
  const initialIncludedContextsRef = useRef({});
  const executeSessionLoadedPromptRef = useRef(null);
  const promptEditorRef = useRef(null);
  const approvedEntryRunRef = useRef(false);
  const executeSessionStorageKey = useMemo(() => {
    if (executeSessionScopeKey) return `prompt-execute-session:${executeSessionScopeKey}`;
    return prompt?.id ? `prompt-execute-session:${prompt.id}` : null;
  }, [executeSessionScopeKey, prompt?.id]);

  const updateExecuteSessionCache = useCallback((mode, nextSessionState) => {
    if (mode !== 'chat' && mode !== 'creator') return;
    setExecuteSessionByMode((prev) => {
      if (preserveExecuteSessionAcrossPrompts) {
        const shared = nextSessionState || null;
        return { chat: shared, creator: shared };
      }
      return { ...prev, [mode]: nextSessionState || null };
    });
  }, [preserveExecuteSessionAcrossPrompts]);

  // Fetch user profile when opening temporary context builder
  useEffect(() => {
    if ((showTemporaryContextBuilder || showConvertTempContextBuilder) && !temporaryContextBuilderUserProfile) {
      getProfile({ force: false }).then(profile => {
        const profileData = profile || null;
        setTemporaryContextBuilderUserProfile(profileData);
      }).catch(err => console.error('Error loading user profile:', err));
    }
  }, [showTemporaryContextBuilder, showConvertTempContextBuilder, temporaryContextBuilderUserProfile, getProfile, cachedProfile.lastFetchedAt]);

  // Keep user groups in sync when parent provides preloaded groups.
  useEffect(() => {
    if (Array.isArray(initialUserGroups)) {
      setUserGroups(initialUserGroups);
    }
  }, [initialUserGroups]);

  // Fetch user groups only when not provided by parent.
  useEffect(() => {
    if (Array.isArray(initialUserGroups)) return;

    const fetchUserGroups = async () => {
      if (!user?.companyId || !user?.id) return;
      
      try {
        const nextGroups = await getGroups({ force: false });
        setUserGroups(nextGroups || []);
      } catch (error) {
        console.error('Error fetching user groups:', error);
        setUserGroups([]);
      }
    };
    
    if (user) {
      fetchUserGroups();
    }
  }, [user, initialUserGroups, getGroups, cachedGroups.lastFetchedAt]);

  // Update local prompt when prop changes
  useEffect(() => {
    setLocalPrompt(prompt);
  }, [prompt]);

  // Optionally auto-open the Execute panel (used by Prompt Builder "Test Prompt")
  // When enabled, open in initialExecuteMode ('chat' | 'creator') so the user
  // lands directly in an active Execute experience instead of an idle panel.
  useEffect(() => {
    if (open && autoOpenExecutePanel) {
      setExecuteMode(initialExecuteMode === 'creator' ? 'creator' : 'chat');
      setShowExecutePanel(true);
      setRunExecuteOnOpen(autoRunExecuteOnOpen !== false);
      setShowMobileContextShelf(true);
      setCreatorMobilePane(initialExecuteMode === 'creator' ? 'document' : 'chat');
      if (initialIncludedContexts && typeof initialIncludedContexts === 'object' && Object.keys(initialIncludedContexts).length > 0) {
        setIncludedContexts(initialIncludedContexts);
      }
    }
  }, [open, autoOpenExecutePanel, autoRunExecuteOnOpen, initialExecuteMode, initialIncludedContexts]);

  // When dialog opens with autoOpenExecutePanel, ensure we run execute once (user shouldn't have to click Go)
  // initialExecuteMode is already applied above; runExecuteOnOpen is set true so PromptExecuteChatPanel runs execute() on mount

  // Execute panel is intentionally instant (no animation)

  // Reset edited text only on prompt changes or explicit saves/resets, not on close.
  useEffect(() => {
    if (!prompt) {
      setEditedPromptText('');
      setOriginalPromptText('');
      setHasManualEdits(false);
      hasManualEditsRef.current = false;
      previousPromptIdRef.current = null;
      return;
    }

    if (!open) {
      approvedEntryRunRef.current = false;
      variableGatePendingRef.current = null;
      setVariableMissingGateOpen(false);
      // Close transient UI only. Keep editor state keyed by previousPromptIdRef so reopening
      // the same prompt resumes unsaved edits.
      setFullScreenOpen(false);
      setFullScreenEditedText('');
      setFullScreenOriginalText('');
      setShowComments(false); // Close comment accordion when dialog closes
      setShowTemporaryContextBuilder(false);
      setShowAttachExistingContextSelector(false);
      setEditingAttachedContext(null);
      setSaveMenuAnchor(null);
      setIsSavingPrompt(false);
      setShowTempContextConvertDialog(false);
      setPendingSaveAction(null);
      setPendingTempContexts([]);
      setShowConvertTempContextBuilder(false);
      setActiveTempContextToConvert(null);
      setTempContextConversionQueue([]);
      setConvertedTempContextMap({});
      setShowMobileContextShelf(false);
      setCreatorMobilePane('document');
      if (!preserveExecuteSessionAcrossPrompts) {
        setExecuteMode('chat');
        setCreatorDocumentContent('');
        setExecuteSessionByMode({ chat: null, creator: null });
        executeSessionLoadedPromptRef.current = null;
      }
      return;
    }
    
    // Only reset when prompt ID actually changes (new prompt), not when prompt data is refreshed
    // This preserves user edits when onRefresh updates the prompt data
    const currentPromptId = prompt?.id;
    const revision = `${currentPromptId}:${prompt.versionNumber ?? ''}:${prompt.updatedAt ?? ''}`;
    const canonicalText = prompt.content || prompt.prompt || '';
    const savedRevisionChanged = savedRevisionRef.current !== null
      && savedRevisionRef.current.key !== revision && savedRevisionRef.current.text !== canonicalText;
    savedRevisionRef.current = { key: revision, text: canonicalText };
    
    if (previousPromptIdRef.current !== currentPromptId) {
      // New prompt - reset everything
      previousPromptIdRef.current = currentPromptId;
      setHasManualEdits(false);
      hasManualEditsRef.current = false;
      setFullScreenEditedText('');
      setFullScreenOriginalText('');
      setShowComments(!!initialShowComments); // Expand comments when requested (e.g. from project card)
      setTemporaryContexts([]); // Clear session-only contexts when switching to a different prompt
      setShowAttachExistingContextSelector(false);
      setEditingAttachedContext(null);
      setShowExecutePanel(!!autoOpenExecutePanel);
      setRunExecuteOnOpen(!!autoOpenExecutePanel && autoRunExecuteOnOpen !== false);
      setShowMobileContextShelf(!!autoOpenExecutePanel);
      setCreatorMobilePane('document');
      setPromptReadMode('preview');
      setDescriptionExpanded(false);
      // When opening with autoOpenExecutePanel (e.g. test modal), preserve initialExecuteMode
      if (!preserveExecuteSessionAcrossPrompts) {
        setExecuteMode(autoOpenExecutePanel && initialExecuteMode === 'creator' ? 'creator' : 'chat');
        setCreatorDocumentContent('');
        setExecuteSessionByMode({ chat: null, creator: null });
        executeSessionLoadedPromptRef.current = null;
      }
      
      // Initialize from the canonical template. `constructedPrompt` may contain substituted
      // variable values, which would erase clickable {{variable}} tokens in the editor.
      const promptText = prompt.content || prompt.prompt || constructedPrompt || '';
      const restoreBase = prompt.discoverySourceContent || promptText;
      if (promptText) {
        setOriginalPromptText(restoreBase);
        setEditedPromptText(promptText);
      } else {
        // Clear if no prompt text available
        setOriginalPromptText('');
        setEditedPromptText('');
      }
    } else if (savedRevisionChanged || (!hasManualEditsRef.current && !hasManualEdits)) {
      if (savedRevisionChanged) {
        setHasManualEdits(false);
        hasManualEditsRef.current = false;
        setFullScreenEditedText('');
        setFullScreenOriginalText('');
      }
      const promptText = prompt.content || prompt.prompt || '';
      if (promptText && promptText !== originalPromptText) {
        const restoreBase = prompt.discoverySourceContent || promptText;
        setOriginalPromptText(restoreBase);
        setEditedPromptText(promptText);
      }
    }
    // If prompt ID hasn't changed, don't reset - preserve user edits
  }, [open, prompt, constructedPrompt, originalPromptText, autoOpenExecutePanel, autoRunExecuteOnOpen, initialExecuteMode, initialShowComments, preserveExecuteSessionAcrossPrompts, hasManualEdits]);

  useEffect(() => {
    if (!open) return;
    const recommended = resolveRecommendedModel(prompt || {});
    if (recommended) setExecuteSelectedModel(recommended);
  }, [open, prompt?.id, prompt?.aiProvider, prompt?.intelligenceLevel, prompt?.model]);

  useEffect(() => {
    if (!open || !prompt?.discoverySourceContent || !onDiscoveryRevisionChange || !prompt?.id) return;
    onDiscoveryRevisionChange(prompt.id, editedPromptText);
  }, [open, editedPromptText, onDiscoveryRevisionChange, prompt?.discoverySourceContent, prompt?.id]);

  // Rehydrate execute-mode state for this prompt within the current browser session.
  useEffect(() => {
    if (!open || !prompt?.id || !executeSessionStorageKey) return;
    if (executeSessionLoadedPromptRef.current === executeSessionStorageKey) return;
    executeSessionLoadedPromptRef.current = executeSessionStorageKey;
    try {
      const raw = window.sessionStorage.getItem(executeSessionStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.executeSessionByMode) {
        if (preserveExecuteSessionAcrossPrompts) {
          const shared = parsed.executeSessionByMode.chat || parsed.executeSessionByMode.creator || null;
          setExecuteSessionByMode({ chat: shared, creator: shared });
        } else {
          setExecuteSessionByMode({
            chat: parsed.executeSessionByMode.chat || null,
            creator: parsed.executeSessionByMode.creator || null,
          });
        }
      }
      if (typeof parsed?.creatorDocumentContent === 'string') {
        setCreatorDocumentContent(parsed.creatorDocumentContent);
      }
      if (typeof parsed?.executeSelectedModel === 'string' && parsed.executeSelectedModel) {
        setExecuteSelectedModel(parsed.executeSelectedModel);
      }
      // Explicit launch mode (Project Execute -> Creator/Chat) must win over cached session mode.
      if (autoOpenExecutePanel) {
        setExecuteMode(initialExecuteMode === 'creator' ? 'creator' : 'chat');
      } else if (parsed?.executeMode === 'chat' || parsed?.executeMode === 'creator') {
        setExecuteMode(parsed.executeMode);
      }
    } catch (e) {
      // no-op: ignore malformed cache
    }
  }, [open, prompt?.id, autoOpenExecutePanel, initialExecuteMode, executeSessionStorageKey, preserveExecuteSessionAcrossPrompts]);

  useEffect(() => {
    if (!open || !prompt?.id || !executeSessionStorageKey) return;
    try {
      window.sessionStorage.setItem(
        executeSessionStorageKey,
        JSON.stringify({
          executeSessionByMode,
          creatorDocumentContent,
          executeSelectedModel,
          executeMode,
        })
      );
    } catch (e) {
      // no-op: storage may be unavailable
    }
  }, [
    open,
    prompt?.id,
    executeSessionStorageKey,
    executeSessionByMode,
    creatorDocumentContent,
    executeSelectedModel,
    executeMode,
  ]);

  const handleVersionRestore = async (version) => {
    try {
      // Call the refresh callback if provided
      if (onRefresh) {
        await onRefresh(prompt.id);
      }
      
      // Show success message (this could be improved with a toast notification)
      
      // Close the version history dialog but keep the main dialog open
      setShowVersionHistory(false);
    } catch (error) {
      console.error('Error refreshing prompt after restore:', error);
    }
  };

  // Load comments when dialog opens (to show count in button)
  useEffect(() => {
    if (open && prompt) {
      // Load comments immediately when dialog opens so we can show count in button
      // Always reload when dialog opens or prompt changes to ensure fresh data
      loadComments();
    }
  }, [open, prompt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also reload when comments section is opened (in case comments changed)
  useEffect(() => {
    if (showComments && prompt && comments.length > 0) {
      // Reload comments when section is opened to ensure we have latest
      loadComments();
    }
  }, [showComments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset comment state when dialog closes
  useEffect(() => {
    if (!open) {
      // Clear comments when dialog closes
      setComments([]);
      setCurrentVersionComments([]);
      setCurrentVersion(null);
      setCommentContent('');
    }
  }, [open]);

  // Reset comment state when prompt changes
  useEffect(() => {
    if (prompt) {
      setComments([]);
      setCurrentVersionComments([]);
      setCurrentVersion(null);
      setCommentContent('');
      setExpandedContexts({}); // Reset expanded contexts
      setHasManualEdits(false); // Reset manual edits flag
      hasManualEditsRef.current = false;
      // Do not clear temporaryContexts here - prompt object reference changes on re-renders (e.g. switching to Execute).
      // Temporary contexts are cleared only when dialog closes or prompt ID changes (see effect above).
      
      // Initialize all contexts as included by default, but exclude inaccessible ones
      if (prompt.contexts && prompt.contexts.length > 0) {
        const initialIncluded = {};
        prompt.contexts.forEach(context => {
          if (context.id) {
            initialIncluded[context.id] = context.hasAccess !== false;
          }
        });
        if (initialIncludedContexts && typeof initialIncludedContexts === 'object') {
          Object.entries(initialIncludedContexts).forEach(([contextId, included]) => {
            if (Object.prototype.hasOwnProperty.call(initialIncluded, contextId)) {
              initialIncluded[contextId] = included !== false;
            }
          });
        }
        setIncludedContexts(initialIncluded);
        initialIncludedContextsRef.current = initialIncluded;
      } else {
        setIncludedContexts({});
        initialIncludedContextsRef.current = {};
      }
      
      // Don't load comments immediately - only load when comments section is opened
    }
  }, [prompt?.id, initialIncludedContexts]); // Reset when prompt ID changes

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      // Step 1: Load all comments (refresh to ensure we have latest)
      const allCommentsResponse = await getPromptComments(prompt.id);
      const allComments = allCommentsResponse.comments || [];
      setComments(allComments);
      
      // Step 2: Determine current version ID if we don't have it
      let versionIdToUse = currentVersion;
      
      if (prompt.versionNumber && !versionIdToUse) {
        try {
          const { getPromptVersions } = await import('../../../services/api');
          const versionsResponse = await getPromptVersions(prompt.id);
          
          if (versionsResponse.versions && versionsResponse.versions.length > 0) {
            // Find the version that matches the current versionNumber
            const currentVersionData = versionsResponse.versions.find(v => v.versionNumber === prompt.versionNumber);
            
            if (currentVersionData) {
              versionIdToUse = currentVersionData.id;
              setCurrentVersion(versionIdToUse);
            } else {
              // Fallback: use the latest version
              const latestVersion = versionsResponse.versions[0]; // They're sorted by version number desc
              versionIdToUse = latestVersion.id;
              setCurrentVersion(versionIdToUse);
            }
          }
        } catch (versionError) {
          console.error('Error fetching versions:', versionError);
          // Continue without versionId - will filter comments without versionId
        }
      }
      
      // Step 3: Filter comments for current version
      if (versionIdToUse) {
        // Filter by specific version ID
        const filteredCurrentVersionComments = allComments.filter(c => c.versionId === versionIdToUse);
        setCurrentVersionComments(filteredCurrentVersionComments);
      } else {
        // No version ID available - show comments without versionId (they're for current/latest version)
        // If all comments have versionId, show all (fallback)
        const commentsWithoutVersion = allComments.filter(c => !c.versionId);
        if (commentsWithoutVersion.length > 0) {
          setCurrentVersionComments(commentsWithoutVersion);
        } else {
          // All comments have versionId but we don't know current version - show all as fallback
          setCurrentVersionComments(allComments);
        }
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  }, [prompt, currentVersion]);

  // Refresh comments when tab changes or when prompt data changes (e.g., after restore)
  useEffect(() => {
    if (showComments && prompt && currentVersion) {
      // Only refresh current version comments when switching to that tab
      if (commentTab === 0) {
        loadComments();
      }
    }
  }, [commentTab, prompt?.versionNumber, currentVersion, showComments, prompt, loadComments]);

  const handleSubmitComment = async () => {
    if (!commentContent.trim()) return;

    setSubmittingComment(true);
    try {
      // Submit comment with current version ID if we have it
      const versionId = currentVersion || null;
      await createComment(prompt.id, commentContent.trim(), versionId);
      setCommentContent('');
      
      // Reload all comments to ensure we have the latest
      await loadComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleContextPreview = (contextId) => {
    setExpandedContexts(prev => prev[contextId] ? {} : { [contextId]: true });
  };

  // Request access handlers
  const handleRequestContextAccess = (context) => {
    setRequestAccessContext(context);
    setShowRequestAccessDialog(true);
    // Generate direct link to the context
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/contexts/${context.id}`;
    setShareableLink(link);
  };

  const handleCloseRequestAccessDialog = () => {
    setShowRequestAccessDialog(false);
    setRequestAccessContext(null);
    setShareableLink('');
  };

  const handleCopyShareLink = async () => {
    if (!shareableLink) return;
    
    try {
      await navigator.clipboard.writeText(shareableLink);
      setRequestAccessSnackbar({
        open: true,
        message: 'Share link copied to clipboard!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error copying link:', error);
      setRequestAccessSnackbar({
        open: true,
        message: 'Error copying link to clipboard',
        severity: 'error'
      });
    }
  };

  // Combined contexts (prompt's linked + session-only temporary) for shelf and execution
  const allContexts = useMemo(() => {
    const base = (localPrompt || prompt)?.contexts || [];
    return [...base, ...(temporaryContexts || [])];
  }, [localPrompt, prompt, temporaryContexts]);

  // Match the existing knowledge update authorization: creator, admin, or co-owner.
  const canEditAttachedContext = useCallback((context) => {
    if (!user || !context) return false;
    if (context.userId === user.id || user.role === 'ADMIN' || user.role === 'OWNER') return true;

    const canDelete = Array.isArray(context.permissions?.canDelete)
      ? context.permissions.canDelete
      : [];
    if (canDelete.includes(user.id) || (user.teamId && canDelete.includes(user.teamId))) return true;

    return (userGroups || [])
      .map((group) => group?.id)
      .filter(Boolean)
      .some((groupId) => canDelete.includes(groupId));
  }, [user, userGroups]);

  const handleEditAttachedContext = useCallback(async (context) => {
    try {
      const response = await getContext(context.id);
      const latestContext = response?.context || response?.data?.context || response?.data || response;
      if (!latestContext?.id) {
        throw new Error('Knowledge record was not found');
      }

      setEditingAttachedContext({
        ...latestContext,
        contextType: latestContext.source === 'UPLOADED_FILE'
          ? 'file_load'
          : latestContext.source === 'WEB_URL'
            ? 'web'
            : 'direct_input',
      });
    } catch (error) {
      console.error('Error loading attached knowledge for editing:', error);
      setRequestAccessSnackbar({
        open: true,
        message: 'Failed to load knowledge for editing',
        severity: 'error',
      });
    }
  }, []);

  const handleSaveAttachedContext = useCallback(async ({ contextData, contextPreview }) => {
    if (!editingAttachedContext?.id) return;

    const permissions = contextData.permissions || {};
    const payload = {
      name: contextData.name,
      description: contextData.description,
      content: contextData.content || contextPreview || '',
      source: contextData.source || (contextData.contextType === 'file_load'
        ? 'UPLOADED_FILE'
        : contextData.contextType === 'web'
          ? 'WEB_URL'
          : 'MANUAL_ENTRY'),
      sourceFileName: contextData.sourceFileName || '',
      sourceFileType: contextData.sourceFileType || '',
      webUrl: contextData.webUrl || '',
      tags: contextData.tags || [],
      category: contextData.category || 'General',
      collectionId: contextData.collectionId || null,
      collectionName: contextData.collectionName || null,
      permissions: {
        companyAccess: permissions.companyAccess === true,
        teamAccess: Array.isArray(permissions.teamAccess) ? permissions.teamAccess : [],
        groupAccess: Array.isArray(permissions.groupAccess) ? permissions.groupAccess : [],
        individualAccess: Array.isArray(permissions.individualAccess) ? permissions.individualAccess : [],
        canEdit: Array.isArray(permissions.canEdit) ? permissions.canEdit : [],
        canShare: Array.isArray(permissions.canShare) ? permissions.canShare : [],
        canDelete: Array.isArray(permissions.canDelete) ? permissions.canDelete : [],
      },
    };
    const response = await updateContext(editingAttachedContext.id, payload);
    const updatedContext = response?.context || response?.data?.context || response?.data || response;
    if (!updatedContext?.id) {
      throw new Error('Knowledge update did not return a record');
    }

    setLocalPrompt((current) => ({
      ...(current || {}),
      contexts: (current?.contexts || []).map((context) => (
        context.id === updatedContext.id ? { ...context, ...updatedContext } : context
      )),
    }));
    setEditingAttachedContext(null);
    setRequestAccessSnackbar({ open: true, message: 'Knowledge updated', severity: 'success' });
  }, [editingAttachedContext]);

  const recommendedPlatformLabel = useMemo(() => {
    const activePrompt = localPrompt || prompt;
    if (!promptHasUsagePlatformMetadata(activePrompt)) return '';
    return formatUsagePlatformLabel({
      whereToUse: activePrompt.whereToUse,
      usagePlatform: activePrompt.usagePlatform,
      usagePlatformOther: activePrompt.usagePlatformOther,
    });
  }, [localPrompt, prompt]);

  const addSessionTemporaryContexts = useCallback(async (contexts) => {
    const nextContexts = (contexts || []).filter((context) => context?.id);
    if (!nextContexts.length) return [];
    setTemporaryContexts((prev) => {
      const merged = new Map(prev.map((context) => [String(context.id), context]));
      nextContexts.forEach((context) => {
        merged.set(String(context.id), { ...context, category: context.category || 'Temporary', hasAccess: true });
      });
      return [...merged.values()];
    });
    setIncludedContexts((prev) => {
      const next = { ...prev };
      nextContexts.forEach((context) => {
        next[context.id] = true;
      });
      return next;
    });
    return nextContexts;
  }, []);

  const replaceTemporaryContextsFromSelection = useCallback(async (selection) => {
    const baseContextIds = new Set(
      ((localPrompt || prompt)?.contexts || []).map((context) => context?.id).filter(Boolean)
    );
    const currentTemporaryContexts = new Map(
      (temporaryContexts || []).map((context) => [context.id, context])
    );
    const selectedById = new Map();
    (Array.isArray(selection) ? selection : [selection]).forEach((contextFile) => {
      const id = contextFile?.contextId || contextFile?.id;
      if (id && !baseContextIds.has(id)) {
        selectedById.set(id, contextFile);
      }
    });

    const nextTemporaryContexts = await Promise.all(
      [...selectedById.entries()].map(async ([id, contextFile]) => {
        const existing = currentTemporaryContexts.get(id);
        let name = contextFile.name || contextFile.contextName || existing?.name || 'Untitled';
        let content = contextFile.content ?? existing?.content ?? '';

        if (!String(content).trim()) {
          try {
            const response = await getContext(id);
            const context = response?.context || response?.data?.context || response?.data || response;
            if (context) {
              name = context.name || name;
              content = context.content ?? content;
            }
          } catch (error) {
            console.error('Error loading selected temporary knowledge:', error);
          }
        }

        // Session-only attachments belong in the Temporary shelf folder, regardless of
        // their source library category.
        return { ...existing, id, name, content, category: 'Temporary', hasAccess: true };
      })
    );

    const previousTemporaryIds = new Set(currentTemporaryContexts.keys());
    setTemporaryContexts(nextTemporaryContexts);
    setIncludedContexts((previous) => {
      const next = { ...previous };
      previousTemporaryIds.forEach((id) => delete next[id]);
      nextTemporaryContexts.forEach((context) => {
        next[context.id] = true;
      });
      return next;
    });
  }, [localPrompt, prompt, temporaryContexts]);

  const isPromptDirty = useMemo(() => {
    const baseText = originalPromptText || '';
    const currentText = editedPromptText || '';
    const textDirty = currentText !== baseText;
    const tempDirty = (temporaryContexts?.length || 0) > 0;
    const baseline = initialIncludedContextsRef.current || {};
    const hasToggledInclude = Object.keys(includedContexts || {}).some((id) => {
      const before = baseline[id] !== false;
      const now = includedContexts[id] !== false;
      return before !== now;
    });
    return textDirty || tempDirty || hasToggledInclude;
  }, [editedPromptText, originalPromptText, temporaryContexts, includedContexts]);

  // Memoize the full prompt text (context + prompt) for execution and "copy prompt + context" only.
  // Display in the dialog shows only the prompt (editedPromptText).
  const fullPromptText = useMemo(() => {
    if (!prompt) return '';
    
    const basePromptPart = editedPromptText?.trim() 
      || constructedPrompt 
      || (localPrompt || prompt).prompt 
      || (localPrompt || prompt).content 
      || '';

    // Always apply variable values to the base prompt so execution matches what the user sees.
    const promptPart = buildExecutionPromptWithVariables(
      basePromptPart,
      prompt.variables || [],
      variableValues || {}
    );
    
    const selectedContexts = allContexts.filter(context =>
      includedContexts[context.id] !== false
    );

    const contextCategoryById = (localPrompt || prompt)?.contextCategories || {};

    return buildFullExecutionPayload({
      contexts: selectedContexts,
      contextCategoryById,
      promptPart,
    });
  }, [prompt, localPrompt, allContexts, includedContexts, constructedPrompt, editedPromptText, variableValues]);

  // Display uses only the prompt (no embedded context)
  const promptOnlyText = useMemo(() => {
    const base =
      editedPromptText?.trim() ||
      constructedPrompt ||
      (localPrompt || prompt)?.prompt ||
      (localPrompt || prompt)?.content ||
      '';

    return buildPromptWithVariables(
      base,
      mergeVariablesFromTemplate(
        editedPromptText?.trim() ||
          (localPrompt || prompt)?.prompt ||
          (localPrompt || prompt)?.content ||
          '',
        (localPrompt || prompt)?.variables || prompt?.variables || []
      ),
      variableValues || {}
    );
  }, [editedPromptText, constructedPrompt, localPrompt, prompt, variableValues]);

  const promptTemplateText = useMemo(() => (
    editedPromptText?.trim() ||
    (localPrompt || prompt)?.prompt ||
    (localPrompt || prompt)?.content ||
    constructedPrompt ||
    ''
  ), [editedPromptText, localPrompt, prompt, constructedPrompt]);

  const getPromptTemplateBase = useCallback(() => {
    return promptTemplateText;
  }, [promptTemplateText]);

  const buildExecutionPayloadsForVariableMerge = useCallback(
    (mergedVals, variablesOverride) => {
      const vars = variablesOverride ?? prompt?.variables ?? [];
      const base = getPromptTemplateBase();
      const promptPart = buildExecutionPromptWithVariables(base, vars, mergedVals || {});
      const selectedContexts = allContexts.filter((context) => includedContexts[context.id] !== false);
      const contextCategoryById = (localPrompt || prompt)?.contextCategories || {};
      const fullPayload = buildFullExecutionPayload({
        contexts: selectedContexts,
        contextCategoryById,
        promptPart,
      });
      return { fullPayload, promptOnlySubstituted: promptPart };
    },
    [getPromptTemplateBase, prompt, allContexts, includedContexts, localPrompt]
  );

  const runWithResolvedVariables = useCallback(
    (baseText, variablesList, run) => {
      const vars = variablesList ?? prompt?.variables ?? [];
      const variablesForTemplate = mergeVariablesFromTemplate(baseText || '', vars);
      if (!variablesForTemplate.length) {
        void run({ ...(variableValues || {}) });
        return;
      }
      if (!onVariableChange) {
        void run({ ...(variableValues || {}) });
        return;
      }
      variableGatePendingRef.current = run;
      setVariableMissingGateRows(variablesForTemplate);
      setVariableMissingGateDraft(
        Object.fromEntries(
          variablesForTemplate.map((v) => [
            v.name,
            variableValues && variableValues[v.name] != null
              ? String(variableValues[v.name])
              : String(v.defaultValue || ''),
          ])
        )
      );
      setVariableMissingGateOpen(true);
    },
    [variableValues, onVariableChange, prompt]
  );

  const cancelVariableMissingGate = useCallback(() => {
    variableGatePendingRef.current = null;
    setVariableMissingGateOpen(false);
    setVariableMissingGateRows([]);
    setVariableMissingGateDraft({});
  }, []);

  const confirmVariableMissingGate = useCallback(() => {
    const run = variableGatePendingRef.current;
    variableGatePendingRef.current = null;
    setVariableMissingGateOpen(false);
    const rows = variableMissingGateRows;
    const draft = variableMissingGateDraft;
    setVariableMissingGateRows([]);
    setVariableMissingGateDraft({});
    if (!run) return;
    const patch = {};
    rows.forEach((v) => {
      patch[v.name] = draft[v.name] ?? '';
    });
    const merged = { ...(variableValues || {}), ...patch };
    Object.keys(patch).forEach((k) => {
      onVariableChange?.(k, patch[k]);
    });
    void run(merged);
  }, [variableMissingGateRows, variableMissingGateDraft, variableValues, onVariableChange]);

  const incrementCopyUsage = useCallback(async () => {
    if (localPrompt?.id && onUsageIncrementProp) {
      try {
        await onUsageIncrementProp(localPrompt.id);
      } catch (e) {
        /* no-op */
      }
    }
  }, [localPrompt, onUsageIncrementProp]);

  const copyPromptOnlyToClipboard = useCallback(
    async (merged) => {
      try {
        const base = getPromptTemplateBase();
        const text = buildExecutionPromptWithVariables(base, prompt?.variables || [], merged);
        await navigator.clipboard.writeText(text || '');
        await incrementCopyUsage();
        setCopySuccessSnackbar({ open: true, message: 'Capability copied' });
      } catch (e) {
        setCopySuccessSnackbar({ open: true, message: 'Failed to copy', severity: 'error' });
      }
    },
    [getPromptTemplateBase, prompt, incrementCopyUsage]
  );

  const copyPromptWithContextToClipboard = useCallback(
    async (merged) => {
      try {
        const { fullPayload } = buildExecutionPayloadsForVariableMerge(merged);
        await navigator.clipboard.writeText(fullPayload || '');
        await incrementCopyUsage();
        setCopySuccessSnackbar({ open: true, message: 'Capability + knowledge copied' });
      } catch (e) {
        setCopySuccessSnackbar({ open: true, message: 'Failed to copy', severity: 'error' });
      }
    },
    [buildExecutionPayloadsForVariableMerge, incrementCopyUsage]
  );

  const requestCopyPromptOnly = useCallback(() => {
    const base = getPromptTemplateBase();
    runWithResolvedVariables(base, prompt?.variables, copyPromptOnlyToClipboard);
  }, [getPromptTemplateBase, runWithResolvedVariables, prompt, copyPromptOnlyToClipboard]);

  const requestCopyPromptWithContext = useCallback(() => {
    const base = getPromptTemplateBase();
    runWithResolvedVariables(base, prompt?.variables, copyPromptWithContextToClipboard);
  }, [getPromptTemplateBase, runWithResolvedVariables, prompt, copyPromptWithContextToClipboard]);

  const copyMcpHandoff = useCallback(async () => {
    setMobileFooterActionsAnchor(null);
    try {
      await navigator.clipboard.writeText(buildCapabilityMcpHandoff(localPrompt || prompt, window.location.origin));
      setCopySuccessSnackbar({ open: true, message: 'Copied. Paste this message into your integrated AI tool and send it.' });
    } catch (error) {
      setCopySuccessSnackbar({ open: true, message: 'Failed to copy', severity: 'error' });
    }
  }, [localPrompt, prompt]);

  const wrapExecutePanelAction = useCallback(
    (doExecute) => {
      if (approvedEntryRunRef.current) {
        approvedEntryRunRef.current = false;
        doExecute();
        return;
      }
      const base = getPromptTemplateBase();
      runWithResolvedVariables(base, prompt?.variables, (merged) => {
        executionPayloadOverrideRef.current = buildExecutionPayloadsForVariableMerge(merged);
        doExecute();
      });
    },
    [getPromptTemplateBase, runWithResolvedVariables, prompt, buildExecutionPayloadsForVariableMerge]
  );

  // Prompts list for execute-mode left panel (extensible for multi-prompt / creator mode)
  const executeModePrompts = useMemo(() => {
    if (Array.isArray(executePrompts) && executePrompts.length > 0) {
      return executePrompts.map((p) => ({
        id: p.id,
        name: p.title || p.name || 'Untitled capability',
        description: p.description || '',
        capabilityType: p.capabilityType || 'prompt',
        promptText:
          p.id === (localPrompt || prompt)?.id
            ? (promptOnlyText ?? fullPromptText ?? '')
            : (p.content || p.prompt || ''),
        rawPromptTemplate: p.content || p.prompt || '',
        variables: p.variables || [],
        variableCount: p.variables?.length || 0,
      }));
    }
    const p = localPrompt || prompt;
    if (!p) return [];
    return [{
      id: p.id,
      name: p.title || p.name || 'Untitled capability',
      description: p.description || '',
      capabilityType: p.capabilityType || 'prompt',
      promptText: promptOnlyText ?? fullPromptText ?? '',
      rawPromptTemplate: p.content || p.prompt || '',
      variables: p.variables || [],
      variableCount: p.variables?.length || 0,
    }];
  }, [executePrompts, localPrompt, prompt, promptOnlyText, fullPromptText]);

  // No longer sync original/edited from fullPromptText - we keep them prompt-only for display/editing
  // (removed effect that overwrote with fullPromptText)

  // Sync full-screen text when opening full-screen modal - use prompt-only text
  useEffect(() => {
    if (fullScreenOpen && prompt && promptTemplateText !== undefined) {
      if (!fullScreenInitializedRef.current) {
        const currentDisplayText = editedPromptText?.trim() ? editedPromptText : promptTemplateText;
        const currentOriginalText = originalPromptText || promptTemplateText;
        setFullScreenOriginalText(currentOriginalText);
        setFullScreenEditedText(currentDisplayText);
        fullScreenInitializedRef.current = true;
      }
    } else if (!fullScreenOpen) {
      fullScreenInitializedRef.current = false;
    }
  }, [fullScreenOpen, prompt, promptTemplateText, editedPromptText, originalPromptText]);

  // Update fullscreen text when variables change (if no manual edits) - prompt only
  useEffect(() => {
    if (fullScreenOpen && promptTemplateText) {
      const hasManualEdits = fullScreenEditedText && 
                             fullScreenEditedText !== fullScreenOriginalText &&
                             fullScreenEditedText !== promptTemplateText;
      if (!hasManualEdits) {
        setFullScreenOriginalText(promptTemplateText);
        setFullScreenEditedText(promptTemplateText);
      }
    }
  }, [fullScreenOpen, promptTemplateText, fullScreenOriginalText, fullScreenEditedText]);

  // Close full-screen and sync state back to main view (used for both Exit button and Dialog onClose/backdrop)
  const handleCloseFullScreen = useCallback(() => {
    setEditedPromptText(fullScreenEditedText || fullScreenOriginalText);
    setOriginalPromptText(fullScreenOriginalText);
    const hadEdits = fullScreenEditedText && fullScreenEditedText.trim() !== '' && fullScreenEditedText !== fullScreenOriginalText;
    if (hadEdits) {
      hasManualEditsRef.current = true;
      setHasManualEdits(true);
    }
    setFullScreenOpen(false);
  }, [fullScreenEditedText, fullScreenOriginalText]);

  const currentPrompt = localPrompt || prompt;
  const menuIsFavorited = interactionsVersion > 0
    ? interactions.favorited.includes(currentPrompt?.id)
    : !!currentPrompt?.isFavorited;
  const currentPromptVariables = useMemo(
    () => {
      const sourceText = fullScreenOpen
        ? (fullScreenEditedText || fullScreenOriginalText || promptTemplateText || currentPrompt?.content || currentPrompt?.prompt || '')
        : (editedPromptText || currentPrompt?.content || currentPrompt?.prompt || '');
      return mergeVariablesFromTemplate(sourceText, currentPrompt?.variables || prompt?.variables || []);
    },
    [fullScreenOpen, fullScreenEditedText, fullScreenOriginalText, promptTemplateText, editedPromptText, currentPrompt, prompt?.variables]
  );
  const filledInputCount = currentPromptVariables.filter((variable) => String(variableValues[variable.name] ?? variable.defaultValue ?? '').trim()).length;
  const inputStatusLabel = `Inputs · ${filledInputCount}/${currentPromptVariables.length} filled`;
  const sourceFileName = String(currentPrompt?.sourceFileName || currentPrompt?.fileName || '');
  const sourceExtension = sourceFileName.includes('.')
    ? sourceFileName.split('.').pop().toLowerCase()
    : '';
  const isUnsupportedPromptSourceEdit = Boolean(sourceExtension) && !EDITABLE_PROMPT_SOURCE_EXTENSIONS.has(sourceExtension);
  const handlePromptZipExport = async () => {
    if (!currentPrompt) return;
    if (onExport) {
      onExport(currentPrompt);
      return;
    }
    try {
      const JSZip = (await import('jszip')).default;
      const rootFolderName = sanitizeExportPathSegment(currentPrompt.title || currentPrompt.name || 'prompt', 'prompt');
      const zip = new JSZip();
      const root = zip.folder(rootFolderName);
      const promptText = editedPromptText || currentPrompt.prompt || currentPrompt.content || '';
      root.file(`${rootFolderName}.md`, `--description--\n${currentPrompt.description || ''}\n\n--prompt--\n${promptText}`);
      const categoryMap = currentPrompt.contextCategories || {};
      const usedCtxNames = new Map();
      (allContexts || [])
        .filter((ctx) => ctx?.id && includedContexts[ctx.id] !== false)
        .forEach((ctx) => {
          const folderKey = sanitizeExportPathSegment(categoryMap[ctx.id] || ctx.category || 'uncategorized', 'uncategorized');
          const dir = root.folder(folderKey);
          const originalName = ctx.sourceFileName || ctx.name || 'context.md';
          const extensionMatch = String(originalName).match(/(\.[A-Za-z0-9_-]+)$/);
          const extension = extensionMatch ? extensionMatch[1] : '.md';
          const base = sanitizeExportPathSegment(String(originalName).replace(/\.[^.]+$/, '') || ctx.name || 'context', 'context');
          const k = `${folderKey}/${base}`;
          const n = (usedCtxNames.get(k) || 0) + 1;
          usedCtxNames.set(k, n);
          const fname = n > 1 ? `${base}-${n}` : base;
          dir.file(`${fname}${extension}`, String(ctx.content || ctx.description || ''));
        });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rootFolderName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setCopySuccessSnackbar({ open: true, message: 'Capability download started', severity: 'success' });
    } catch (error) {
      setCopySuccessSnackbar({ open: true, message: error?.message || 'Capability export failed', severity: 'error' });
    }
  };
  const normalizedCurrentPermissions = useMemo(
    () => normalizePromptPermissions(currentPrompt?.permissions),
    [currentPrompt?.permissions]
  );
  const userGroupIds = userGroups.map(g => g.id);
  const permissions = currentPrompt?.permissions || {};
  const canEdit = permissions.canEdit || [];
  const canDelete = permissions.canDelete || [];
  const isCreator = currentPrompt?.userId === user?.id;
  const canViewVersionHistory = !!(user && currentPrompt && (
    isCreator ||
    canEdit.includes(user.id) ||
    (user.teamId && canEdit.includes(user.teamId)) ||
    (userGroupIds.length > 0 && userGroupIds.some(groupId => canEdit.includes(groupId))) ||
    canDelete.includes(user.id) ||
    (user.teamId && canDelete.includes(user.teamId)) ||
    (userGroupIds.length > 0 && userGroupIds.some(groupId => canDelete.includes(groupId)))
  ));
  const canSaveCurrentPrompt = canViewVersionHistory;
  const canManagePromptPermissions = !!(
    currentPrompt?.id &&
    user &&
    resolveCanEditPromptPermissions(currentPrompt, user)
  );
  const isCustomPermissionDraft = permissionDraftLevel === 'custom';
  const hasUnsavedPermissionChanges = canManagePromptPermissions
    && (JSON.stringify(permissionDraftPermissions) !== JSON.stringify(normalizedCurrentPermissions)
      || (permissionDraftLevel === 'community') !== !!currentPrompt?.publishToCommunity);

  useEffect(() => {
    if (!open) {
      setIsPermissionDraftDirty(false);
    }
  }, [open]);

  useEffect(() => {
    if (!currentPrompt?.id) {
      setPermissionDraftLevel('private');
      setPermissionDraftPermissions(normalizePromptPermissions(null));
      setPermissionAccessList([]);
      setIsPermissionDraftDirty(false);
      return;
    }
    if (isPermissionDraftDirty) return;
    const normalizedPermissions = normalizePromptPermissions(currentPrompt.permissions);
    setPermissionDraftPermissions(normalizedPermissions);
    setPermissionDraftLevel(inferModalPermissionLevel(currentPrompt.publishToCommunity, normalizedPermissions));
  }, [currentPrompt?.id, currentPrompt?.permissions, currentPrompt?.publishToCommunity, isPermissionDraftDirty, open]);

  const permissionLevels = useMemo(() => ({
    'view-only': {
      label: 'View',
      icon: <InfoIcon />,
      description: 'Can view the capability only',
      permissions: ['view']
    },
    interact: {
      label: 'Interact',
      icon: <LikeIcon />,
      description: 'Can view, comment, clone, and share',
      permissions: ['view', 'comment', 'clone', 'share']
    },
    edit: {
      label: 'Edit',
      icon: <EditIcon />,
      description: 'Can edit capability content',
      permissions: ['view', 'comment', 'clone', 'share', 'edit', 'version-history']
    },
    owner: {
      label: 'Co-owner',
      icon: <DeleteIcon />,
      description: 'Can fully manage capability permissions and deletion',
      permissions: ['view', 'comment', 'clone', 'share', 'edit', 'version-history', 'delete']
    }
  }), []);

  const rebuildPermissionAccessList = useCallback(async (permissionsToMap) => {
    if (!user?.companyId) {
      setPermissionAccessList([]);
      return;
    }
    const mappedPermissions = normalizePromptPermissions(permissionsToMap);
    const getRoleForEntity = (entityId) => {
      if (mappedPermissions.canDelete.includes(entityId)) return 'owner';
      if (mappedPermissions.canEdit.includes(entityId)) return 'edit';
      if (mappedPermissions.viewOnlyAccess.includes(entityId)) return 'view-only';
      return 'interact';
    };

    try {
      const [usersResponse, teamsResponse, groupsResponse] = await Promise.all([
        listCompanyUsers(user.companyId).catch(() => ({ users: [] })),
        listTeams(user.companyId).catch(() => ({ teams: [] })),
        getGroups({ force: false }).then((groups) => ({ groups })).catch(() => ({ groups: [] }))
      ]);
      const allUsers = usersResponse.users || [];
      const allTeams = teamsResponse.teams || [];
      const allGroups = groupsResponse.groups || [];
      const creatorId = currentPrompt?.userId || user?.id;
      const nextAccessList = [];

      mappedPermissions.individualAccess.forEach((id) => {
        if (creatorId && id === creatorId) return;
        const match = allUsers.find((u) => u.id === id);
        if (match) {
          nextAccessList.push({
            id: match.id,
            type: 'user',
            name: match.name || `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.email,
            email: match.email,
            role: getRoleForEntity(id),
            description: match.role || 'USER'
          });
        }
      });

      mappedPermissions.teamAccess.forEach((id) => {
        const match = allTeams.find((t) => t.id === id);
        if (match) {
          nextAccessList.push({
            id: match.id,
            type: 'team',
            name: match.name,
            role: getRoleForEntity(id),
            description: match.description || '',
            memberCount: match.memberCount || 0
          });
        }
      });

      mappedPermissions.groupAccess.forEach((id) => {
        const match = allGroups.find((g) => g.id === id);
        if (match) {
          nextAccessList.push({
            id: match.id,
            type: 'group',
            name: match.name,
            role: getRoleForEntity(id),
            description: match.description || '',
            memberCount: match.memberCount || 0
          });
        }
      });
      setPermissionAccessList(nextAccessList);
    } catch (error) {
      console.error('Error rebuilding prompt permission access list:', error);
      setPermissionAccessList([]);
    }
  }, [currentPrompt?.userId, getGroups, user?.companyId, user?.id]);

  useEffect(() => {
    if (permissionDraftLevel !== 'custom') {
      setPermissionAccessList([]);
      return;
    }
    rebuildPermissionAccessList(permissionDraftPermissions);
  }, [permissionDraftLevel, permissionDraftPermissions, rebuildPermissionAccessList]);

  const headerMoreActionsVisible = !showExecutePanel;

  const buildPromptSavePayload = useCallback((contextTransform = {}) => {
    const selectedContexts = (allContexts || []).filter((ctx) => includedContexts[ctx.id] !== false);
    const skipTempIds = new Set(contextTransform.skipTempIds || []);
    const resolvedIds = [];
    const baseCategoryMap = { ...(currentPrompt?.contextCategories || {}) };

    selectedContexts.forEach((ctx) => {
      if (!ctx?.id) return;
      if (String(ctx.id).startsWith('temp-')) {
        if (skipTempIds.has(ctx.id)) return;
        const converted = contextTransform.convertedTempContextMap?.[ctx.id];
        if (!converted?.id) return;
        resolvedIds.push(converted.id);
        if (!baseCategoryMap[converted.id]) {
          baseCategoryMap[converted.id] = converted.category || 'General';
        }
        return;
      }
      resolvedIds.push(ctx.id);
      if (!baseCategoryMap[ctx.id]) {
        baseCategoryMap[ctx.id] = 'General';
      }
    });

    const contextIds = Array.from(new Set(resolvedIds));
    const contextCategories = contextIds.reduce((acc, id) => {
      acc[id] = baseCategoryMap[id] || 'General';
      return acc;
    }, {});
    const promptText = editedPromptText || currentPrompt?.content || currentPrompt?.prompt || '';

    return {
      title: currentPrompt?.title || '',
      description: currentPrompt?.description || '',
      category: currentPrompt?.category || '',
      prompt: promptText,
      content: promptText,
      variables: mergeVariablesFromTemplate(promptText, Array.isArray(currentPrompt?.variables) ? currentPrompt.variables : []),
      tags: Array.isArray(currentPrompt?.tags) ? currentPrompt.tags : [],
      contextIds,
      contextCategories,
      contextFolderNames: Array.isArray(currentPrompt?.contextFolderNames) ? currentPrompt.contextFolderNames : [],
      model: currentPrompt?.model || null,
      allowCopying: currentPrompt?.allowCopying !== false,
      publishToCommunity: !!currentPrompt?.publishToCommunity,
      communityTitle: currentPrompt?.communityTitle || '',
      isMultiStep: !!currentPrompt?.isMultiStep,
      steps: Array.isArray(currentPrompt?.steps) ? currentPrompt.steps : [],
      collectionId: currentPrompt?.collectionId || null,
      collectionName: currentPrompt?.collectionName || null,
      capabilityType: currentPrompt?.capabilityType || 'prompt',
      aiProvider: currentPrompt?.aiProvider || 'openai',
      intelligenceLevel: currentPrompt?.intelligenceLevel || 'medium',
      whereToUse: currentPrompt?.whereToUse || 'chat',
      usagePlatform: currentPrompt?.usagePlatform || 'promptcrew',
      usagePlatformOther: currentPrompt?.usagePlatformOther || '',
      permissions: currentPrompt?.permissions || undefined
    };
  }, [allContexts, includedContexts, currentPrompt, editedPromptText]);

  const buildNextPermissionsForLevel = useCallback((level) => {
    const currentPermissions = normalizePromptPermissions(permissionDraftPermissions);
    const teamId = user?.teamId || null;
    const creatorId = currentPrompt?.userId || user?.id || null;

    if (level === 'community') {
      return applyPromptSharingPreset('community', {
        userTeamId: teamId,
        creatorId,
        prevPermissions: currentPermissions
      }).permissions;
    }
    if (level === 'private') {
      return applyPromptSharingPreset('private', {
        userTeamId: teamId,
        creatorId,
        prevPermissions: currentPermissions
      }).permissions;
    }
    if (level === 'company') {
      return applyPromptSharingPreset('organization', {
        userTeamId: teamId,
        creatorId,
        prevPermissions: currentPermissions
      }).permissions;
    }
    if (level === 'team') {
      return applyPromptSharingPreset('team', {
        userTeamId: teamId,
        creatorId,
        prevPermissions: currentPermissions
      }).permissions;
    }
    if (level === 'group') {
      const firstGroupId = currentPermissions.groupAccess[0] || userGroups[0]?.id || null;
      return {
        companyAccess: false,
        teamAccess: teamId ? [teamId] : [],
        groupAccess: firstGroupId ? [firstGroupId] : [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: [],
        viewOnlyAccess: []
      };
    }
    return applyPromptSharingPreset('custom', {
      userTeamId: teamId,
      creatorId,
      prevPermissions: currentPermissions
    }).permissions;
  }, [currentPrompt?.userId, permissionDraftPermissions, user?.id, user?.teamId, userGroups]);

  const handlePermissionLevelSelect = useCallback(async (level) => {
    const nextPermissions = buildNextPermissionsForLevel(level);
    setIsPermissionDraftDirty(true);
    setPermissionDraftLevel(level);
    setPermissionDraftPermissions(nextPermissions);
    if (level === 'custom') {
      await rebuildPermissionAccessList(nextPermissions);
    }
  }, [buildNextPermissionsForLevel, rebuildPermissionAccessList]);

  const handlePermissionSettingsChange = useCallback((nextPermissions) => {
    const normalizedNextPermissions = normalizePromptPermissions(nextPermissions);
    setIsPermissionDraftDirty(true);
    setPermissionDraftPermissions(normalizedNextPermissions);
    setPermissionDraftLevel((prevLevel) => (
      prevLevel === 'custom'
        ? 'custom'
        : inferModalPermissionLevel(false, normalizedNextPermissions)
    ));
  }, []);

  const handleSavePermissionLevel = useCallback(async () => {
    if (!canManagePromptPermissions || !currentPrompt?.id || isSavingPermissionLevel) return;
    try {
      setIsSavingPermissionLevel(true);
      const permissionUpdate = buildPermissionLevelUpdate(currentPrompt, permissionDraftLevel, permissionDraftPermissions);
      const nextPermissions = permissionUpdate.permissions;
      const nextPublishToCommunity = permissionDraftLevel === 'community';
      const updated = await updatePrompt(currentPrompt.id, permissionUpdate);
      const updatedPrompt = updated?.prompt || updated?.data?.prompt || updated;
      if (updatedPrompt && updatedPrompt.id) {
        setLocalPrompt((prev) => ({ ...(prev || {}), ...updatedPrompt, permissions: nextPermissions, publishToCommunity: nextPublishToCommunity }));
      } else {
        setLocalPrompt((prev) => ({
          ...(prev || {}),
          permissions: nextPermissions,
          publishToCommunity: nextPublishToCommunity
        }));
      }
      onPromptUpdated?.({ ...(currentPrompt || {}), ...(updatedPrompt || {}), permissions: nextPermissions, publishToCommunity: nextPublishToCommunity });
      if (onRefresh) {
        await onRefresh();
      }
      setCopySuccessSnackbar({ open: true, message: 'Permissions saved', severity: 'success' });
      setIsPermissionDraftDirty(false);
      setPermissionSubmenuAnchor(null);
      setHeaderMenuAnchor(null);
    } catch (error) {
      console.error('Error saving prompt permission level:', error);
      setCopySuccessSnackbar({ open: true, message: 'Failed to save permission level', severity: 'error' });
    } finally {
      setIsSavingPermissionLevel(false);
    }
  }, [
    canManagePromptPermissions,
    currentPrompt,
    isSavingPermissionLevel,
    onPromptUpdated,
    onRefresh,
    permissionDraftPermissions,
    permissionDraftLevel
  ]);

  const executeSaveAction = useCallback(async (action, contextTransform = {}) => {
    try {
      setIsSavingPrompt(true);
      const payload = buildPromptSavePayload(contextTransform);

      if (action === 'save') {
        if (!canSaveCurrentPrompt || !currentPrompt?.id) return;
        const updated = await updatePrompt(currentPrompt.id, payload);
        const updatedPrompt = updated?.prompt || updated?.data?.prompt || updated;
        const nextPrompt = updatedPrompt?.id
          ? updatedPrompt
          : { ...(currentPrompt || {}), ...payload, id: currentPrompt.id };
        setLocalPrompt((prev) => ({ ...(prev || {}), ...nextPrompt }));
        onPromptUpdated?.(nextPrompt);
        setCopySuccessSnackbar({ open: true, message: 'Capability saved', severity: 'success' });
        if (onRefresh) {
          await onRefresh();
        }
        setTemporaryContexts([]);
        setPendingSaveAction(null);
        return;
      }

      if (action === 'saveAs') {
        if (onOpenBuilder) {
          onOpenBuilder(payload);
          setPendingSaveAction(null);
        } else {
          localStorage.setItem('clonedPromptData', JSON.stringify(payload));
          window.location.href = '/prompt-builder?mode=clone';
        }
      }
    } catch (error) {
      console.error('Error in save action:', error);
      setCopySuccessSnackbar({ open: true, message: error?.message || 'Failed to save capability', severity: 'error' });
    } finally {
      setIsSavingPrompt(false);
      setShowTempContextConvertDialog(false);
    }
  }, [buildPromptSavePayload, canSaveCurrentPrompt, currentPrompt, onOpenBuilder, onPromptUpdated, onRefresh]);

  const flushPromptEditor = useCallback(() => {
    promptEditorRef.current?.flush?.();
  }, []);

  const handleSaveActionRequest = useCallback((action) => {
    const selectedTemp = (allContexts || []).filter(
      (ctx) => String(ctx?.id || '').startsWith('temp-') && includedContexts[ctx.id] !== false
    );
    setSaveMenuAnchor(null);
    if (selectedTemp.length > 0) {
      setPendingSaveAction(action);
      setPendingTempContexts(selectedTemp);
      setSelectedTempContextIdsToConvert(selectedTemp.map((ctx) => ctx.id));
      setShowTempContextConvertDialog(true);
      return;
    }
    executeSaveAction(action);
  }, [allContexts, includedContexts, executeSaveAction]);

  const handleCloneOriginal = useCallback(() => {
    setSaveMenuAnchor(null);
    onClone?.(prompt);
  }, [onClone, prompt]);

  const resetTempContextConversionState = useCallback(() => {
    tempContextConversionQueueRef.current = [];
    convertedTempContextMapRef.current = {};
    setShowTempContextConvertDialog(false);
    setShowConvertTempContextBuilder(false);
    setActiveTempContextToConvert(null);
    setTempContextConversionQueue([]);
    setConvertedTempContextMap({});
    setTempContextConversionStepKey(0);
    setPendingSaveAction(null);
    setPendingTempContexts([]);
    setSelectedTempContextIdsToConvert([]);
  }, []);

  const handleStartTempContextConversion = useCallback(() => {
    if (!pendingTempContexts.length || !pendingSaveAction) return;
    const selectedIds = new Set(selectedTempContextIdsToConvert);
    const selectedContexts = pendingTempContexts.filter((ctx) => selectedIds.has(ctx.id));
    if (selectedContexts.length === 0) return;
    convertedTempContextMapRef.current = {};
    tempContextConversionQueueRef.current = selectedContexts;
    setConvertedTempContextMap({});
    setTempContextConversionQueue(selectedContexts);
    setActiveTempContextToConvert(selectedContexts[0]);
    setTempContextConversionStepKey((prev) => prev + 1);
    setShowConvertTempContextBuilder(true);
    setShowTempContextConvertDialog(false);
  }, [pendingTempContexts, pendingSaveAction, selectedTempContextIdsToConvert]);

  const handleSkipTempContextsAndContinue = useCallback(async () => {
    const skipped = pendingTempContexts.map((ctx) => ctx.id);
    await executeSaveAction(pendingSaveAction, { skipTempIds: skipped });
    resetTempContextConversionState();
  }, [pendingTempContexts, pendingSaveAction, executeSaveAction, resetTempContextConversionState]);

  const completeActiveTempContextConversion = useCallback(async (convertedContext = null) => {
    if (!activeTempContextToConvert || !pendingSaveAction) return;
    let nextMap = convertedTempContextMapRef.current || {};
    if (convertedContext?.id) {
      nextMap = {
        ...nextMap,
        [activeTempContextToConvert.id]: convertedContext
      };
      convertedTempContextMapRef.current = nextMap;
      setConvertedTempContextMap(nextMap);
    }

    const queue = tempContextConversionQueueRef.current.length
      ? tempContextConversionQueueRef.current
      : tempContextConversionQueue;
    const remaining = queue.filter((ctx) => ctx.id !== activeTempContextToConvert.id);
    if (remaining.length > 0) {
      tempContextConversionQueueRef.current = remaining;
      setTempContextConversionQueue(remaining);
      setActiveTempContextToConvert(remaining[0]);
      setTempContextConversionStepKey((prev) => prev + 1);
      return;
    }

    const convertedIds = new Set(Object.keys(nextMap));
    const skipTempIds = pendingTempContexts
      .filter((ctx) => !convertedIds.has(ctx.id))
      .map((ctx) => ctx.id);
    setShowConvertTempContextBuilder(false);
    setActiveTempContextToConvert(null);
    setTempContextConversionQueue([]);
    await executeSaveAction(pendingSaveAction, { convertedTempContextMap: nextMap, skipTempIds });
    resetTempContextConversionState();
  }, [
    activeTempContextToConvert,
    executeSaveAction,
    pendingSaveAction,
    pendingTempContexts,
    resetTempContextConversionState,
    tempContextConversionQueue
  ]);

  const handleSkipActiveTempContext = useCallback(() => {
    completeActiveTempContextConversion(null);
  }, [completeActiveTempContextConversion]);

  const activeTempContextConversionIndex = useMemo(() => {
    if (!activeTempContextToConvert || tempContextConversionQueue.length === 0) return 0;
    const index = tempContextConversionQueue.findIndex((ctx) => ctx.id === activeTempContextToConvert.id);
    return index >= 0 ? index + 1 : 1;
  }, [activeTempContextToConvert, tempContextConversionQueue]);

  const selectedTempContextCount = selectedTempContextIdsToConvert.length;

  const hasIncludedContextsForCopy = useMemo(
    () => allContexts?.some((c) => includedContexts[c.id] !== false) ?? false,
    [allContexts, includedContexts]
  );
  const isExecuteFooterActive = showExecutePanel;
  const hasFooterActions = showCommentButton || Boolean(showCloneButton && onClone && isPromptDirty);
  const isKnowledgePreviewOpen = allContexts.some((context) => expandedContexts[context.id]);

  const handleRunMode = (mode) => {
    const base = getPromptTemplateBase();
    runWithResolvedVariables(base, prompt?.variables, (merged) => {
      executionPayloadOverrideRef.current = buildExecutionPayloadsForVariableMerge(merged);
      approvedEntryRunRef.current = true;
      setExecuteMode(mode === 'creator' ? 'creator' : 'chat');
      setShowExecutePanel(true);
      setRunExecuteOnOpen(true);
      setShowMobileContextShelf(false);
      setCreatorMobilePane(mode === 'creator' ? 'document' : 'chat');
    });
  };

  const handleExecuteBack = useCallback(() => {
    approvedEntryRunRef.current = false;
    executionPayloadOverrideRef.current = null;
    setShowExecutePanel(false);
    setRunExecuteOnOpen(false);
    setExecuteSessionByMode({ chat: null, creator: null });
    setCreatorDocumentContent('');
    setShowMobileContextShelf(false);
  }, []);

  const mobileKnowledgeToggle = (
    <KnowledgeShelfToggleButton
      inGroup
      active={showMobileContextShelf}
      onClick={() => setShowMobileContextShelf((prev) => !prev)}
    />
  );

  const executeHeaderNavActions = showTestButton ? (
    <Tooltip title="Back to capability">
      <IconButton
        size="small"
        onClick={handleExecuteBack}
        aria-label="Back to capability"
        sx={executeChromeIconButtonInGroupSx()}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const executeHeaderBarProps = {
    layout: 'execute',
    compactChrome: isMobile,
    navActions: executeHeaderNavActions,
    showCloseButton: true,
    onClose,
    contextAction: isMobile ? mobileKnowledgeToggle : null,
  };

  const contextShelfRailProps = {
    showCollapseRail: true,
    ...(isMobile && showMobileContextShelf
      ? { onCollapseRailClick: () => setShowMobileContextShelf(false) }
      : {}),
  };

  if (!prompt) return null;

  /** Build and read views use the shared primary workspace shell. */
  const dialogMaxWidth = showExecutePanel ? false : 'xl';
  const dialogFullScreen = isMobile;
  const dialogPaperProps = showExecutePanel
    ? {
        sx: {
          ...expandedPromptShellPaperSx,
          overflow: 'hidden',
        },
      }
    : isMobile
          ? {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100dvh',
                overflow: 'hidden',
                m: 0,
                borderRadius: 0,
              },
            }
          : {
              sx: {
                ...promptLibraryViewDialogPaperSx,
                overflow: 'hidden',
              },
            };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={dialogMaxWidth}
        fullWidth
        fullScreen={dialogFullScreen}
        PaperProps={dialogPaperProps}
      >
      {customHeader}
      {!showExecutePanel && (
      <DialogTitle
        sx={{
          ...expandedPromptShellTitleSx,
          ...(mobileKeyboardOpen && isMobile ? { display: 'none' } : {}),
          ...(showExecutePanel ? { py: { xs: 1.5, sm: 1 } } : {}),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 0 } }}>
          {closeLabel && <Button startIcon={<ArrowBackIcon />} onClick={onClose} sx={{ alignSelf: 'flex-start', mb: 1 }}>{closeLabel}</Button>}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {!showExecutePanel && (
              <Typography variant="h6" sx={{ minWidth: 0, flex: 1 }}>
                {currentPrompt?.title}
              </Typography>
            )}
            {showExecutePanel && !isMobile && <Box sx={{ flex: 1 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {isMobile && !showExecutePanel && mobileKnowledgeToggle}
              {showExecutePanel && showTestButton && (
                <Tooltip title="Back to capability">
                  <IconButton
                    size="small"
                    onClick={handleExecuteBack}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!showExecutePanel && showEditButton && onEdit && (
                <Tooltip title="Edit capability">
                  <IconButton
                    size="small"
                    aria-label="Edit capability"
                    onClick={() => onEdit(currentPrompt)}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!showExecutePanel && showCloneButton && onClone && !isPromptDirty && (
                <Tooltip title="Clone capability">
                  <IconButton
                    size="small"
                    aria-label="Clone capability"
                    onClick={() => onClone(prompt)}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' } }}
                  >
                    <CloneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {headerMoreActionsVisible && (
                <IconButton
                  size="small"
                  aria-label="More actions"
                  onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}

              <IconButton aria-label="Close capability" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            <CapabilityTypeInfoChip
              capabilityType={(localPrompt || prompt)?.capabilityType}
              recommendedPlatformLabel={recommendedPlatformLabel}
              isMobile={isMobile}
              iconOnlyOnMobile={false}
            />
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>·</Typography>
            <BuildQualityIndicator
              buildQuality={(localPrompt || prompt)?.buildQuality}
              onFindMissing={isCreator && onEdit
                ? () => onEdit(localPrompt || prompt, { refineMissing: true })
                : onClone ? () => onClone(localPrompt || prompt, { refineMissing: true }) : undefined}
              showLabel
            />
          </Box>
        </Box>
      </DialogTitle>
      )}
      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor) && headerMoreActionsVisible}
        onClose={() => {
          setPermissionSubmenuAnchor(null);
          setHeaderMenuAnchor(null);
        }}
      >
        {onFavorite && currentPrompt?.hasAccess !== false && (
          <MenuItem onClick={() => { onFavorite(currentPrompt.id); setHeaderMenuAnchor(null); }}>
            <ListItemIcon>{menuIsFavorited ? <FavoritedIcon fontSize="small" sx={{ color: '#d4a017' }} /> : <FavoriteIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{menuIsFavorited ? 'Remove from favorites' : 'Add to favorites'}</ListItemText>
          </MenuItem>
        )}
        {canManagePromptPermissions && (
          <MenuItem onClick={(event) => setPermissionSubmenuAnchor(event.currentTarget)}>
            <ListItemIcon><ManagePermissionsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Permissions" />
          </MenuItem>
        )}
        {canViewVersionHistory && (
          <MenuItem onClick={() => { setShowVersionHistory(true); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>
              {currentPrompt?.versionCount > 1
                ? `Version History (v${currentPrompt?.currentVersion || 1} of ${currentPrompt?.versionCount})`
                : 'Version History'}
            </ListItemText>
          </MenuItem>
        )}
        {showShareButton && onShare && (
          <MenuItem onClick={() => { onShare(currentPrompt); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share capability</ListItemText>
          </MenuItem>
        )}
        {currentPrompt?.hasAccess !== false && (
          <MenuItem onClick={() => { handlePromptZipExport(); setHeaderMenuAnchor(null); }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download capability</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setHeaderMenuAnchor(null); openFaq(FAQ_LINKS.capabilities); }}>
          <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Help</ListItemText>
        </MenuItem>
        {showDeleteButton && onDelete && (
          <MenuItem
            onClick={() => {
              setShowDeleteConfirm(true);
              setHeaderMenuAnchor(null);
            }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete capability</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={permissionSubmenuAnchor}
        open={Boolean(permissionSubmenuAnchor)}
        onClose={() => setPermissionSubmenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem disabled>
          <ListItemText primary={`Permission level: ${formatPermissionLevelLabel(permissionDraftLevel)}`} />
        </MenuItem>
        <MenuItem onClick={() => handlePermissionLevelSelect('private')}>
          <ListItemIcon><PrivateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set to Private</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePermissionLevelSelect('team')}>
          <ListItemIcon><TeamIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set to Team</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handlePermissionLevelSelect('company')}>
          <ListItemIcon><CompanyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set to Organization</ListItemText>
        </MenuItem>
        <MenuItem disabled>
          <ListItemIcon><CommunityIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Set to Community" secondary="Use editor instead" />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            await handlePermissionLevelSelect('custom');
            openCustomPermissionRef.current?.();
          }}
        >
          <ListItemIcon><CustomIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Set to Custom</ListItemText>
        </MenuItem>
        {hasUnsavedPermissionChanges && (
          <MenuItem
            onClick={handleSavePermissionLevel}
            disabled={isSavingPermissionLevel}
          >
            <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{isSavingPermissionLevel ? 'Saving permission changes...' : 'Save permission changes'}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <DialogContent
        sx={
          showExecutePanel
            ? {
                p: 0,
                position: 'relative',
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
              }
            : isMobile
              ? {
                  p: 0,
                  position: 'relative',
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  display: 'flex',
                  flexDirection: 'column',
                  pb: 0,
                }
              : {
                  p: 0,
                  position: 'relative',
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  pb: { xs: 0, sm: 2 },
                }
        }
      >
        {showExecutePanel ? (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            {(!isMobile || showMobileContextShelf) && (
              <PromptContextShelf
                contexts={allContexts}
                contextCategories={(localPrompt || prompt)?.contextCategories || {}}
                includedContexts={includedContexts}
                onIncludeChange={(id, checked) => setIncludedContexts(prev => ({ ...prev, [id]: checked }))}
                onPreview={toggleContextPreview}
                onRequestAccess={handleRequestContextAccess}
                onAddTemporaryContext={() => setShowTemporaryContextBuilder(true)}
                onAttachExistingContext={() => setShowAttachExistingContextSelector(true)}
                prompts={executeModePrompts}
                onPromptOpenFullScreen={(nextPrompt) => {
                  if (onExecutePromptSelect && nextPrompt?.id) {
                    onExecutePromptSelect(nextPrompt, { autoRun: false });
                    return;
                  }
                  setFullScreenOpen(true);
                }}
                onPromptExecute={(nextPrompt) => {
                  const cur = localPrompt || prompt;
                  const isCurrent = nextPrompt?.id && nextPrompt.id === cur?.id;
                  if (!isCurrent && onExecutePromptSelect && nextPrompt?.id) {
                    executionPayloadOverrideRef.current = null;
                    onExecutePromptSelect(nextPrompt, { autoRun: true });
                    return;
                  }
                  const base = isCurrent
                    ? (
                        editedPromptText?.trim() ||
                        constructedPrompt ||
                        cur?.prompt ||
                        cur?.content ||
                        ''
                      )
                    : (
                        nextPrompt?.rawPromptTemplate ||
                        nextPrompt?.promptText ||
                        nextPrompt?.prompt ||
                        nextPrompt?.content ||
                        ''
                      );
                  const vars = nextPrompt?.variables || [];
                  runWithResolvedVariables(base, vars, (merged) => {
                    const text = buildExecutionPromptWithVariables(base, vars, merged).trim();
                    if (text) {
                      executionPayloadOverrideRef.current = null;
                      executeChatRef.current?.appendAndRun?.(text);
                      return;
                    }
                    if (onExecutePromptSelect && nextPrompt?.id) {
                      executionPayloadOverrideRef.current = null;
                      onExecutePromptSelect(nextPrompt, { autoRun: true });
                      return;
                    }
                    executionPayloadOverrideRef.current = buildExecutionPayloadsForVariableMerge(merged, vars);
                    executeChatRef.current?.execute();
                  });
                }}
                onOpenVariables={(e) => setVariableMenuAnchor(e.currentTarget)}
                isPromptExecuting={executePanelExecuting}
                activePromptId={(localPrompt || prompt)?.id || null}
                isExecuteView
                layoutMode={executeMode}
                variableValues={variableValues}
                mobileOverlay={isMobile}
                onMobileOverlayClose={() => setShowMobileContextShelf(false)}
                {...contextShelfRailProps}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            {/* Execute mode: Variables Menu for shelf "Variables" button; chat panel takes remaining space */}
          <Menu
            anchorEl={variableMenuAnchor}
            open={Boolean(variableMenuAnchor)}
            onClose={() => setVariableMenuAnchor(null)}
            PaperProps={{
              sx: {
                maxHeight: 400,
                width: 410,
                mt: 1
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Inputs ({currentPromptVariables.length || 0})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {inputStatusLabel}
              </Typography>
            </Box>
            {currentPromptVariables.map((variable) => (
                <MenuItem
                  key={variable.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingVariable(variable);
                    setVariableMenuAnchor(null);
                  }}
                  sx={{ py: 1 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.4 }}>
                      {variable.name.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())}
                    </Typography>
                    {variable.description && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.3,
                          mt: 0.25
                        }}
                      >
                        {variable.description}
                      </Typography>
                    )}
                    <Typography 
                      variant="caption" 
                      color={variableValues[variable.name] ? 'primary.main' : 'text.secondary'}
                      sx={{ 
                        mt: 0.5,
                        fontStyle: variableValues[variable.name] ? 'normal' : 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {variableValues[variable.name] || 'Not set'}
                    </Typography>
                  </Box>
                </MenuItem>
            ))}
          </Menu>

          <VariableValueEditDialog
            open={Boolean(editingVariable)}
            variable={editingVariable}
            initialValue={variableValues[editingVariable?.name] || ''}
            onClose={() => setEditingVariable(null)}
            onSave={(name, val) => {
              // Only update variableValues — keep {{placeholders}} in editedPromptText.
              // Baking values into the template (old applyVariableChangeToText) removed tokens in Prompt mode
              // and could corrupt replacements when values contained $ (String.replace substitution rules).
              onVariableChange(name, val);
            }}
          />

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', flexShrink: 0, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600 }} title={currentPrompt?.title || currentPrompt?.name}>
                  {currentPrompt?.title || currentPrompt?.name || 'Untitled capability'}
                </Typography>
              </Box>
              {executeMode === 'creator' ? (
                isMobile ? (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 0,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <ExecuteHeaderBar
                      executeMode={executeMode}
                      onExecuteModeChange={setExecuteMode}
                      showModeToggle
                      {...executeHeaderBarProps}
                      selectedModel={executeSelectedModel}
                      onModelChange={setExecuteSelectedModel}
                      modelMenuAnchor={executeModelMenuAnchor}
                      onModelMenuAnchorChange={setExecuteModelMenuAnchor}
                      prompt={localPrompt || prompt}
                      onExportDocx={async () => {
                        const markdown = creatorDocumentContent || '';
                        if (!markdown.trim()) return;
                        const rawTitle = (localPrompt || prompt)?.title || (localPrompt || prompt)?.name || 'document';
                        const safeName = rawTitle.replace(/[\s\\/:*?"<>|]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'document';
                        const fileName = `${safeName}.docx`;
                        try {
                          const { blob, fileName: downloadedName } = await exportDocx(markdown, fileName);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = downloadedName;
                          a.click();
                          URL.revokeObjectURL(url);
                          setCopySuccessSnackbar({ open: true, message: 'Document exported', severity: 'success' });
                        } catch (e) {
                          setCopySuccessSnackbar({ open: true, message: e?.message || 'Export failed', severity: 'error' });
                        }
                      }}
                      canExportDocx={!!(creatorDocumentContent && String(creatorDocumentContent).trim())}
                    />
                    <Tabs
                      value={creatorMobilePane}
                      onChange={(_, next) => setCreatorMobilePane(next)}
                      variant="fullWidth"
                      sx={{
                        flexShrink: 0,
                        minHeight: 40,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: '0.8rem' },
                      }}
                    >
                      <Tab label="Document" value="document" />
                      <Tab label="Chat" value="chat" />
                    </Tabs>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflow: 'hidden',
                          display: creatorMobilePane === 'document' ? 'flex' : 'none',
                          flexDirection: 'column',
                        }}
                      >
                        <CreatorRichEditor
                          valueMarkdown={creatorDocumentContent}
                          onChangeMarkdown={(next) => setCreatorDocumentContent(next)}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflow: 'hidden',
                          display: creatorMobilePane === 'chat' ? 'flex' : 'none',
                          flexDirection: 'column',
                        }}
                      >
                        <PromptExecuteChatPanel
                          ref={executeChatRef}
                          prompt={localPrompt || prompt}
                          constructedPrompt={constructedPrompt}
                          includedContexts={includedContexts}
                          allContexts={allContexts}
                          contextCategories={(localPrompt || prompt)?.contextCategories || {}}
                          editedPromptText={editedPromptText}
                          originalPromptText={originalPromptText}
                          isPromptLocked={isPromptLocked}
                          fullPromptText={autoOpenExecutePanel ? '' : fullPromptText}
                          promptOnlyText={promptOnlyText}
                          wrapExecuteAction={wrapExecutePanelAction}
                          executionPayloadOverrideRef={executionPayloadOverrideRef}
                          autoExecute={false}
                          runExecuteOnOpen={runExecuteOnOpen}
                          onExecuteTriggered={() => setRunExecuteOnOpen(false)}
                          onExecutingChange={setExecutePanelExecuting}
                          executeMode={executeMode}
                          onExecuteModeChange={setExecuteMode}
                          variant="full"
                          showHeader={false}
                          chatUiVariant="assistant"
                          suppressAssistantLanding
                          selectedModel={executeSelectedModel}
                          onModelChange={setExecuteSelectedModel}
                          executionSessionState={executeSessionByMode.creator}
                          onExecutionSessionStateChange={(next) => updateExecuteSessionCache('creator', next)}
                          preserveConversationOnExecute={preserveExecuteSessionAcrossPrompts}
                          suppressIntro={runExecuteOnOpen}
                          onRestartSession={() => {
                            if (executeChatRef.current?.clearSession) {
                              executeChatRef.current.clearSession();
                            }
                            setCreatorDocumentContent('');
                          }}
                          onAddTemporaryContext={() => setShowTemporaryContextBuilder(true)}
                          onAttachExistingContext={() => setShowAttachExistingContextSelector(true)}
                          onCreateTemporaryContexts={addSessionTemporaryContexts}
                          onTemporaryContextsCreated={(contexts) => {
                            setCopySuccessSnackbar({
                              open: true,
                              message: `${contexts.length} temporary knowledge item${contexts.length === 1 ? '' : 's'} added`,
                              severity: 'success'
                            });
                          }}
                          creatorDocument={creatorDocumentContent}
                          onCreatorDocumentGenerated={(doc, explanation) => {
                            setCreatorDocumentContent(doc || '');
                          }}
                          onLockPrompt={() => {
                            const currentEditedText = editedPromptText;
                            if (currentEditedText && currentEditedText.trim() !== '') {
                              isLockingRef.current = true;
                              hasManualEditsRef.current = true;
                              setEditedPromptText(currentEditedText);
                              setHasManualEdits(true);
                              setIsPromptLocked(true);
                              setTimeout(() => { isLockingRef.current = false; }, 100);
                            } else {
                              setIsPromptLocked(true);
                            }
                          }}
                          onUsageIncrement={async () => {
                            if (onUsageIncrementProp && (localPrompt?.id || prompt?.id)) {
                              await onUsageIncrementProp(localPrompt?.id || prompt?.id);
                              if (onRefresh && (localPrompt?.id || prompt?.id)) {
                                await onRefresh(localPrompt?.id || prompt?.id);
                              }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                <CreatorExecuteWorkspace
                  isMobile={false}
                  storageKey="promptDialogCreatorChatWidthPx"
                  creatorDocument={creatorDocumentContent}
                  onCreatorDocumentChange={(next) => setCreatorDocumentContent(next)}
                  headerProps={{
                    layout: 'execute',
                    chromePlacement: 'chatColumn',
                    compactChrome: isMobile,
                    navActions: executeHeaderNavActions,
                    showCloseButton: true,
                    onClose,
                    executeMode,
                    onExecuteModeChange: setExecuteMode,
                    showModeToggle: true,
                    selectedModel: executeSelectedModel,
                    onModelChange: setExecuteSelectedModel,
                    modelMenuAnchor: executeModelMenuAnchor,
                    onModelMenuAnchorChange: setExecuteModelMenuAnchor,
                    prompt: localPrompt || prompt,
                    contextAction: null,
                    onExportDocx: async () => {
                      const markdown = creatorDocumentContent || '';
                      if (!markdown.trim()) return;
                      const rawTitle = (localPrompt || prompt)?.title || (localPrompt || prompt)?.name || 'document';
                      const safeName = rawTitle.replace(/[\s\\/:*?"<>|]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'document';
                      const fileName = `${safeName}.docx`;
                      try {
                        const { blob, fileName: downloadedName } = await exportDocx(markdown, fileName);
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = downloadedName;
                        a.click();
                        URL.revokeObjectURL(url);
                        setCopySuccessSnackbar({ open: true, message: 'Document exported', severity: 'success' });
                      } catch (e) {
                        setCopySuccessSnackbar({ open: true, message: e?.message || 'Export failed', severity: 'error' });
                      }
                    },
                    canExportDocx: !!(creatorDocumentContent && String(creatorDocumentContent).trim()),
                  }}
                  chatPanel={
                    <PromptExecuteChatPanel
                      ref={executeChatRef}
                      prompt={localPrompt || prompt}
                      constructedPrompt={constructedPrompt}
                      includedContexts={includedContexts}
                      allContexts={allContexts}
                      contextCategories={(localPrompt || prompt)?.contextCategories || {}}
                      editedPromptText={editedPromptText}
                      originalPromptText={originalPromptText}
                      isPromptLocked={isPromptLocked}
                      fullPromptText={autoOpenExecutePanel ? '' : fullPromptText}
                      promptOnlyText={promptOnlyText}
                      wrapExecuteAction={wrapExecutePanelAction}
                      executionPayloadOverrideRef={executionPayloadOverrideRef}
                      autoExecute={false}
                      runExecuteOnOpen={runExecuteOnOpen}
                      onExecuteTriggered={() => setRunExecuteOnOpen(false)}
                      onExecutingChange={setExecutePanelExecuting}
                      executeMode={executeMode}
                      onExecuteModeChange={setExecuteMode}
                      variant="shelf"
                      showHeader={false}
                      chatUiVariant="assistant"
                      suppressAssistantLanding
                      selectedModel={executeSelectedModel}
                      onModelChange={setExecuteSelectedModel}
                      executionSessionState={executeSessionByMode.creator}
                      onExecutionSessionStateChange={(next) => updateExecuteSessionCache('creator', next)}
                      preserveConversationOnExecute={preserveExecuteSessionAcrossPrompts}
                      suppressIntro={runExecuteOnOpen}
                      onRestartSession={() => {
                        if (executeChatRef.current?.clearSession) {
                          executeChatRef.current.clearSession();
                        }
                        setCreatorDocumentContent('');
                      }}
                      onAddTemporaryContext={() => setShowTemporaryContextBuilder(true)}
                      onAttachExistingContext={() => setShowAttachExistingContextSelector(true)}
                      onCreateTemporaryContexts={addSessionTemporaryContexts}
                      onTemporaryContextsCreated={(contexts) => {
                        setCopySuccessSnackbar({
                          open: true,
                          message: `${contexts.length} temporary knowledge item${contexts.length === 1 ? '' : 's'} added`,
                          severity: 'success'
                        });
                      }}
                      creatorDocument={creatorDocumentContent}
                      onCreatorDocumentGenerated={(doc, explanation) => {
                        setCreatorDocumentContent(doc || '');
                      }}
                      onLockPrompt={() => {
                        const currentEditedText = editedPromptText;
                        if (currentEditedText && currentEditedText.trim() !== '') {
                          isLockingRef.current = true;
                          hasManualEditsRef.current = true;
                          setEditedPromptText(currentEditedText);
                          setHasManualEdits(true);
                          setIsPromptLocked(true);
                          setTimeout(() => { isLockingRef.current = false; }, 100);
                        } else {
                          setIsPromptLocked(true);
                        }
                      }}
                      onUsageIncrement={async () => {
                        if (onUsageIncrementProp && (localPrompt?.id || prompt?.id)) {
                          await onUsageIncrementProp(localPrompt?.id || prompt?.id);
                          if (onRefresh && (localPrompt?.id || prompt?.id)) {
                            await onRefresh(localPrompt?.id || prompt?.id);
                          }
                        }
                      }}
                    />
                  }
                />
                )
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <ExecuteHeaderBar
                    executeMode={executeMode}
                    onExecuteModeChange={setExecuteMode}
                    showModeToggle
                    {...executeHeaderBarProps}
                    selectedModel={executeSelectedModel}
                    onModelChange={setExecuteSelectedModel}
                    modelMenuAnchor={executeModelMenuAnchor}
                    onModelMenuAnchorChange={setExecuteModelMenuAnchor}
                    prompt={localPrompt || prompt}
                  />
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <PromptExecuteChatPanel
                      ref={executeChatRef}
                      prompt={localPrompt || prompt}
                      constructedPrompt={constructedPrompt}
                      includedContexts={includedContexts}
                      allContexts={allContexts}
                      contextCategories={(localPrompt || prompt)?.contextCategories || {}}
                      editedPromptText={editedPromptText}
                      originalPromptText={originalPromptText}
                      isPromptLocked={isPromptLocked}
                      fullPromptText={autoOpenExecutePanel ? '' : fullPromptText}
                      promptOnlyText={promptOnlyText}
                      wrapExecuteAction={wrapExecutePanelAction}
                      executionPayloadOverrideRef={executionPayloadOverrideRef}
                      autoExecute={false}
                      runExecuteOnOpen={runExecuteOnOpen}
                      onExecuteTriggered={() => setRunExecuteOnOpen(false)}
                      onExecutingChange={setExecutePanelExecuting}
                      executeMode={executeMode}
                      onExecuteModeChange={setExecuteMode}
                      showHeader={false}
                      chatUiVariant="assistant"
                      suppressAssistantLanding
                      selectedModel={executeSelectedModel}
                      onModelChange={setExecuteSelectedModel}
                      executionSessionState={executeSessionByMode.chat}
                      onExecutionSessionStateChange={(next) => updateExecuteSessionCache('chat', next)}
                      preserveConversationOnExecute={preserveExecuteSessionAcrossPrompts}
                      suppressIntro={runExecuteOnOpen}
                      onRestartSession={() => {
                        if (executeChatRef.current?.clearSession) {
                          executeChatRef.current.clearSession();
                        }
                        setCreatorDocumentContent('');
                      }}
                      onAddTemporaryContext={() => setShowTemporaryContextBuilder(true)}
                      onAttachExistingContext={() => setShowAttachExistingContextSelector(true)}
                      onCreateTemporaryContexts={addSessionTemporaryContexts}
                      onTemporaryContextsCreated={(contexts) => {
                        setCopySuccessSnackbar({
                          open: true,
                          message: `${contexts.length} temporary knowledge item${contexts.length === 1 ? '' : 's'} added`,
                          severity: 'success'
                        });
                      }}
                      onLockPrompt={() => {
                        // Lock the prompt when executing to preserve edits
                        const currentEditedText = editedPromptText;
                        if (currentEditedText && currentEditedText.trim() !== '') {
                          isLockingRef.current = true;
                          hasManualEditsRef.current = true;
                          setEditedPromptText(currentEditedText);
                          setHasManualEdits(true);
                          setIsPromptLocked(true);
                          setTimeout(() => { isLockingRef.current = false; }, 100);
                        } else {
                          setIsPromptLocked(true);
                        }
                      }}
                      onUsageIncrement={async () => {
                        if (onUsageIncrementProp && (localPrompt?.id || prompt?.id)) {
                          await onUsageIncrementProp(localPrompt?.id || prompt?.id);
                          if (onRefresh && (localPrompt?.id || prompt?.id)) {
                            await onRefresh(localPrompt?.id || prompt?.id);
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row', flex: { xs: 'none', sm: 1 }, minWidth: 0, overflow: { xs: 'visible', sm: 'hidden' } }}>
            {(!isMobile || showMobileContextShelf) && (
              <PromptContextShelf
                contexts={allContexts}
                contextCategories={(localPrompt || prompt)?.contextCategories || {}}
                includedContexts={includedContexts}
                onIncludeChange={(id, checked) => setIncludedContexts(prev => ({ ...prev, [id]: checked }))}
                onPreview={toggleContextPreview}
                onRequestAccess={handleRequestContextAccess}
                onAddTemporaryContext={() => setShowTemporaryContextBuilder(true)}
                onAttachExistingContext={() => setShowAttachExistingContextSelector(true)}
                mobileOverlay={isMobile}
                onMobileOverlayClose={() => setShowMobileContextShelf(false)}
                {...contextShelfRailProps}
                useExecuteChrome
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', sm: 'auto' }, px: 2, pt: { xs: 0.5, sm: 1 }, pb: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ mb: 1.5, mx: -2, px: 2 }}>
              {(() => {
                const p = localPrompt || prompt;
                const d = (p?.description || '').trim();
                const h = (p?.howToUse || '').trim();
                const combined = [d, h].filter(Boolean).join('\n\n');
                const needsCollapse = descriptionNeedsCollapse(combined);
                if (!combined) {
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      No Capability Details
                    </Typography>
                  );
                }
                return (
                  <Box>
                    <Box sx={{ px: 2, mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={capabilityInstructionsTitleSx}>
                        Description
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          overflow: 'hidden',
                        }}
                      >
                        <MarkdownPreviewEditor
                          value={combined}
                          mode="preview"
                          compact
                          minRows={1}
                          contentTextSx={instructionBodyTextSx}
                          previewSx={{ px: 2, py: 0.75, '& .markdown-body': {
                            ...capabilityDescriptionPreviewMarkdownSx,
                            ...(!descriptionExpanded && needsCollapse ? {
                              display: '-webkit-box',
                              WebkitLineClamp: DESCRIPTION_COLLAPSE_MAX_LINES,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              '& > *': { display: 'inline', margin: 0 },
                              '& > *::after': { content: '" "' },
                            } : {}),
                          } }}
                        />
                      </Box>
                      {needsCollapse && (
                        <Button
                          size="small"
                          onClick={() => setDescriptionExpanded((prev) => !prev)}
                          sx={{
                            mt: 0.5,
                            ml: 2,
                            px: 0,
                            minWidth: 0,
                            textTransform: 'none',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                          }}
                        >
                          {descriptionExpanded ? 'Show Less' : 'Show More'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })()}
            </Box>

            {/* Variables Menu Popup */}
            <Menu
              anchorEl={variableMenuAnchor}
              open={Boolean(variableMenuAnchor)}
              onClose={() => setVariableMenuAnchor(null)}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  width: 410,
                  mt: 1
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Inputs ({currentPromptVariables.length || 0})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {inputStatusLabel}
                </Typography>
              </Box>
              {currentPromptVariables.map((variable) => (
                  <MenuItem
                    key={variable.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingVariable(variable);
                      setVariableMenuAnchor(null);
                    }}
                    sx={{ py: 1 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.4 }}>
                        {variable.name.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())}
                      </Typography>
                      {variable.description && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            mt: 0.25
                          }}
                        >
                          {variable.description}
                        </Typography>
                      )}
                      <Typography 
                        variant="caption" 
                        color={variableValues[variable.name] ? 'primary.main' : 'text.secondary'}
                        sx={{ 
                          mt: 0.5,
                          fontStyle: variableValues[variable.name] ? 'normal' : 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {variableValues[variable.name] || 'Not set'}
                      </Typography>
                    </Box>
                  </MenuItem>
              ))}
            </Menu>

            <VariableValueEditDialog
              open={Boolean(editingVariable)}
              variable={editingVariable}
              initialValue={variableValues[editingVariable?.name] || ''}
              onClose={() => setEditingVariable(null)}
              onSave={(name, val) => {
                onVariableChange(name, val);
              }}
            />

            {/* Prompt card - unified header + editor + tags (matches Build tab layout) */}
            <Box sx={{ mb: 2, mx: -2, px: 2 }}>
              <Box sx={{ ...promptCardShellSx, width: '100%' }}>
                <Box
                  sx={{
                    ...promptCardHeaderSx,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 1 },
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    flexShrink: 0,
                    px: { xs: 1, sm: 2 },
                    py: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ ...capabilityInstructionsTitleSx, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    AI Prompt
                  </Typography>
                  <Box data-markdown-clickaway-ignore="true" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 0.25, sm: 0.75 }, flexWrap: 'wrap', minWidth: 0, ml: 'auto' }}>
                    <EditPreviewToggle
                      mode={promptReadMode}
                      sx={{ '& .MuiTab-root': { px: { xs: 0.5, sm: 1.25 }, fontSize: { xs: '0.75rem', sm: '0.8125rem' } } }}
                      onBeforeModeChange={async () => {
                        if (promptReadMode === 'edit') {
                          await flushPromptEditor?.();
                        }
                      }}
                      onModeChange={setPromptReadMode}
                    />
                    {currentPromptVariables.length > 0 && (
                      <Tooltip title={inputStatusLabel}>
                        <Button
                          size="small"
                          aria-label={inputStatusLabel}
                          onClick={(e) => {
                            e.stopPropagation();
                            setVariableMenuAnchor(e.currentTarget);
                          }}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {`Inputs ${filledInputCount}/${currentPromptVariables.length}`}
                        </Button>
                      </Tooltip>
                    )}
                    {isPromptLocked && editedPromptText && editedPromptText !== originalPromptText && (
                      <Tooltip title="Restore original AI prompt">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditedPromptText(originalPromptText);
                            setHasManualEdits(false);
                            hasManualEditsRef.current = false;
                            if (currentPromptVariables.length && onVariableChange) {
                              currentPromptVariables.forEach((v) => {
                                if (v && v.name) onVariableChange(v.name, '');
                              });
                            }
                          }}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'warning.main',
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Expand to full screen">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentDisplayText = editedPromptText?.trim() ? editedPromptText : promptOnlyText;
                          const currentOriginalText = originalPromptText || promptOnlyText;
                          setFullScreenOriginalText(currentOriginalText);
                          setFullScreenEditedText(currentDisplayText);
                          fullScreenInitializedRef.current = false;
                          setFullScreenOpen(true);
                        }}
                        sx={{
                          display: { xs: 'none', sm: 'inline-flex' },
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...promptContentGlowEmbeddedSx,
                    maxHeight: 'none',
                    minHeight: { xs: 0, sm: 300 },
                    overflow: 'visible',
                  }}
                >
                    <CapabilityInstructionBody
                      value={editedPromptText || ''}
                      onChange={(text) => {
                        setEditedPromptText(text);
                        setLocalPrompt((prev) => ({
                          ...(prev || currentPrompt || {}),
                          variables: mergeVariablesFromTemplate(text, currentPromptVariables),
                        }));
                        setHasManualEdits(true);
                        hasManualEditsRef.current = true;
                      }}
                      mode={promptReadMode}
                      onModeChange={setPromptReadMode}
                      onBeforePreview={flushPromptEditor}
                      keepEditorMounted
                      editorControlRef={promptEditorRef}
                      clickAwayToPreview
                      contentTextSx={instructionBodyTextSx}
                      minRows={10}
                      variables={currentPromptVariables}
                      variableValues={variableValues}
                      onVariableClick={(variable) => {
                        setEditingVariable(variable);
                      }}
                      onBlur={flushPromptEditor}
                      embedded
                      lockedSourceExtension={isUnsupportedPromptSourceEdit ? sourceExtension : ''}
                      previewSx={{ '& .markdown-body': capabilityInstructionPreviewMarkdownSx }}
                    />
                </Box>

              {(localPrompt || prompt)?.tags && (localPrompt || prompt).tags.length > 0 && (
                <Box
                  sx={{
                    ...promptCardFooterSx,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    py: 1,
                    px: 2,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      lineHeight: 1.45,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  >
                    Tags
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {(localPrompt || prompt).tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}
              </Box>
            </Box>

            {/* Comments Section */}
            {showComments && (
              <Accordion defaultExpanded={true} sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Comments
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                
                {/* Comment Tabs */}
                <Tabs 
                  value={commentTab} 
                  onChange={(e, newValue) => setCommentTab(newValue)}
                  sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab 
                    label={`Current Version (${currentVersionComments.length})`}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  />
                  <Tab 
                    label={`All Comments (${comments.length})`}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  />
                </Tabs>

                {/* Reddit-style Comments Container */}
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                  {/* Comment Input */}
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Share your feedback, suggestions, or questions..."
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      disabled={submittingComment}
                      sx={{ mb: 2 }}
                      variant="outlined"
                      size="small"
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmitComment}
                        disabled={!commentContent.trim() || submittingComment}
                        sx={{ minWidth: 100 }}
                      >
                        {submittingComment ? 'Adding...' : 'Comment'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Comments List */}
                  {loadingComments ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (() => {
                    const displayComments = commentTab === 0 ? currentVersionComments : comments;
                    return displayComments.length > 0 ? (
                      <Box>
                        {displayComments.map((comment, index) => (
                          <Box 
                            key={comment.id}
                            sx={{ 
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              p: 2,
                              borderBottom: index < displayComments.length - 1 ? '1px solid' : 'none',
                              borderColor: 'grey.200',
                              '&:hover': {
                                bgcolor: 'grey.50'
                              }
                            }}
                          >
                            {/* User Avatar */}
                            <Avatar 
                              sx={{ 
                                bgcolor: 'primary.main', 
                                color: 'primary.contrastText',
                                width: 32, 
                                height: 32,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {getUserInitials({
                                ...(comment.author || {}),
                                name: comment.author?.name && comment.author.name !== 'Unknown' ? comment.author.name : (comment.userName || comment.author?.name || comment.userId),
                                email: comment.author?.email || comment.userEmail,
                                id: comment.author?.id || comment.userId,
                              })}
                            </Avatar>
                            
                            {/* Comment Content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                  {getUserDisplayName({
                                    ...(comment.author || {}),
                                    name: comment.author?.name && comment.author.name !== 'Unknown' ? comment.author.name : (comment.userName || comment.author?.name || comment.userId),
                                    email: comment.author?.email || comment.userEmail,
                                    id: comment.author?.id || comment.userId,
                                  })}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {formatDate(comment.createdAt)}
                                </Typography>
                                {comment.author?.isCurrentUser && (
                                  <Chip 
                                    label="you" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.7rem', fontWeight: 500 }}
                                  />
                                )}
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  lineHeight: 1.5,
                                  color: 'text.primary',
                                  whiteSpace: 'pre-wrap'
                                }}
                              >
                                {comment.content}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {commentTab === 0 ? 'No comments on current version yet.' : 'No comments yet.'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            </Box>
          </Box>
        )}

        {allContexts?.filter((context) => expandedContexts[context.id]).map((context) => (
          <KnowledgePreview
            key={context.id}
            context={context}
            onBack={() => toggleContextPreview(context.id)}
            backLabel={showExecutePanel ? 'Back to workspace' : 'Back to AI prompt'}
            canEdit={canEditAttachedContext(context)}
            onEdit={handleEditAttachedContext}
          />
        ))}
      </DialogContent>
      {!showExecutePanel && !isKnowledgePreviewOpen && (
      <DialogActions
        sx={{
          ...(mobileKeyboardOpen && isMobile ? { display: 'none' } : {}),
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          flexShrink: 0,
          mt: { xs: 0, sm: 'auto' },
          ...(isMobile
            ? {
                display: mobileKeyboardOpen ? 'none' : 'block',
                px: 1.5,
                py: 1,
                pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }
            : {
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1.5 },
              }),
        }}
      >
        {isMobile ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                alignItems: 'stretch',
                boxSizing: 'border-box',
              }}
            >
              {hasFooterActions && <Button
                variant="outlined"
                fullWidth={false}
                size="small"
                endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                onClick={(e) => setMobileFooterActionsAnchor(e.currentTarget)}
                sx={{
                  minHeight: 44,
                  flex: '1 1 0',
                  minWidth: 0,
                  maxWidth: '100%',
                  textTransform: 'none',
                  px: 1,
                  '& .MuiButton-endIcon': { ml: 0.5 },
                }}
              >
                Actions
              </Button>}
              <CapabilityRunButton
                isMobile
                capabilityType={(localPrompt || prompt)?.capabilityType}
                showExecution={showTestButton}
                hasKnowledge={hasIncludedContextsForCopy}
                onCopy={requestCopyPromptOnly}
                onCopyWithKnowledge={requestCopyPromptWithContext}
                onUseInTool={copyMcpHandoff}
                onSelectMode={handleRunMode}
                buttonSx={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}
              />
            </Box>
            <Menu
              anchorEl={mobileFooterActionsAnchor}
              open={Boolean(mobileFooterActionsAnchor)}
              onClose={() => setMobileFooterActionsAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              PaperProps={{ sx: { minWidth: 280, maxHeight: '70vh' } }}
            >
              {showExecutePanel && showTestButton && (
                <MenuItem
                  onClick={() => {
                    setMobileFooterActionsAnchor(null);
                    if (executeMode === 'creator') {
                      onClose?.();
                      return;
                    }
                    setShowExecutePanel(false);
                    setRunExecuteOnOpen(false);
                    setExecuteSessionByMode({ chat: null, creator: null });
                    setCreatorDocumentContent('');
                    setShowMobileContextShelf(false);
                  }}
                >
                  <ListItemIcon><ArrowBackIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Back to capability" />
                </MenuItem>
              )}
              {!isExecuteFooterActive && showCommentButton && (
                <MenuItem
                  onClick={() => {
                    setMobileFooterActionsAnchor(null);
                    setShowComments(!showComments);
                  }}
                >
                  <ListItemIcon><ChatBubbleIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={showComments ? 'Hide comments' : (comments.length > 0 ? `Comments (${comments.length})` : 'Comments')}
                  />
                </MenuItem>
              )}
              {!isExecuteFooterActive && showCloneButton && onClone && isPromptDirty && canSaveCurrentPrompt && (
                <MenuItem onClick={() => { setMobileFooterActionsAnchor(null); handleSaveActionRequest('save'); }} disabled={isSavingPrompt}>
                  <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Save" secondary="Update this capability in place" />
                </MenuItem>
              )}
              {!isExecuteFooterActive && showCloneButton && onClone && isPromptDirty && (
                <MenuItem onClick={() => { setMobileFooterActionsAnchor(null); handleSaveActionRequest('saveAs'); }} disabled={isSavingPrompt}>
                  <ListItemIcon><CloneIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Save as" />
                </MenuItem>
              )}
              {!isExecuteFooterActive && showCloneButton && onClone && isPromptDirty && (
                <MenuItem onClick={() => { setMobileFooterActionsAnchor(null); handleCloneOriginal(); }} disabled={isSavingPrompt}>
                  <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Clone original" />
                </MenuItem>
              )}
            </Menu>
          </>
        ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            gap: 2,
            width: '100%'
          }}
        >
          {/* Left side - Action buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap'
            }}
          >
            {!isExecuteFooterActive && showCommentButton && (
              <Button
                variant={showComments ? "contained" : "outlined"}
                size="medium"
                startIcon={<ChatBubbleIcon fontSize="small" />}
                onClick={() => setShowComments(!showComments)}
                sx={{
                  minWidth: 80,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50'
                  },
                  '& .MuiSvgIcon-root': {
                    fill: 'none !important',
                    stroke: 'currentColor',
                    strokeWidth: 1.5
                  }
                }}
              >
                {showComments ? 'Hide Comments' : (comments.length > 0 ? `Comments (${comments.length})` : (prompt?.comments ? `Comments (${prompt.comments})` : 'Comments'))}
              </Button>
            )}
            {!isExecuteFooterActive && showCloneButton && onClone && isPromptDirty && (
              <>
                <Tooltip title="Save edited capability options" arrow>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<SaveIcon fontSize="small" />}
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
                    disabled={isSavingPrompt}
                    sx={{
                      minWidth: 80,
                      '&:hover': {
                        borderColor: 'success.main',
                        backgroundColor: 'success.50'
                      }
                    }}
                  >
                    {isSavingPrompt ? 'Saving...' : 'Save'}
                  </Button>
                </Tooltip>
                <Menu
                  anchorEl={saveMenuAnchor}
                  open={Boolean(saveMenuAnchor)}
                  onClose={() => setSaveMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {canSaveCurrentPrompt && (
                    <MenuItem onClick={() => handleSaveActionRequest('save')} disabled={isSavingPrompt}>
                      <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Save" secondary="Update this capability in place" />
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => handleSaveActionRequest('saveAs')} disabled={isSavingPrompt}>
                    <ListItemIcon><CloneIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Save as" secondary="Create a new capability from current edits" />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleCloneOriginal} disabled={isSavingPrompt}>
                    <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Clone original" secondary="Ignore edits and clone source capability" />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          <CapabilityRunButton
            capabilityType={(localPrompt || prompt)?.capabilityType}
            showExecution={showTestButton}
            hasKnowledge={hasIncludedContextsForCopy}
            onCopy={requestCopyPromptOnly}
            onCopyWithKnowledge={requestCopyPromptWithContext}
            onUseInTool={copyMcpHandoff}
            onSelectMode={handleRunMode}
          />
        </Box>
        )}
      </DialogActions>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="error">
            Delete capability
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The capability will be permanently deleted.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete "{(localPrompt || prompt)?.title}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <Button 
            onClick={() => setShowDeleteConfirm(false)}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: isMobile ? 'auto' : 80 }}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={async () => {
              try {
                if (onDelete) {
                  await Promise.resolve(onDelete(currentPrompt));
                }
                setShowDeleteConfirm(false);
                onClose();
              } catch (err) {
                console.error('Error deleting prompt:', err);
                setShowDeleteConfirm(false);
              }
            }}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: isMobile ? 'auto' : 80 }}
            fullWidth={isMobile}
          >
            Delete Capability
          </Button>
        </DialogActions>
      </Dialog>

      <RuntimeInputsDialog
        open={variableMissingGateOpen}
        onClose={cancelVariableMissingGate}
        capability={currentPrompt}
        variables={variableMissingGateRows}
        values={variableMissingGateDraft}
        onChange={(name, value) => setVariableMissingGateDraft((prev) => ({ ...prev, [name]: value }))}
        onConfirm={confirmVariableMissingGate}
      />

      {/* Execute is now embedded inside this modal (right-side panel) */}

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        prompt={prompt}
        onRestore={handleVersionRestore}
      />

      {/* Copy Success Snackbar */}
      <Portal>
      <Snackbar
        open={copySuccessSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setCopySuccessSnackbar({ ...copySuccessSnackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySuccessSnackbar({ ...copySuccessSnackbar, open: false })} 
          severity={copySuccessSnackbar.severity || 'success'}
          sx={{ width: '100%' }}
        >
          {copySuccessSnackbar.message}
        </Alert>
      </Snackbar>
      </Portal>

      {/* Full Screen Modal */}
      <Dialog
        open={fullScreenOpen}
        onClose={handleCloseFullScreen}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: expandedPromptShellPaperSx,
        }}
      >
        {/* Fixed header section with title and exit button */}
        <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', py: 1, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box data-markdown-clickaway-ignore="true" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              {/* Prompt Name */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mr: 1
                }}
              >
                {(localPrompt || prompt)?.title || 'Untitled capability'}
              </Typography>
              {/* Restore button - show when there are manual text edits (not just variable changes) */}
              {fullScreenEditedText && fullScreenEditedText !== fullScreenOriginalText && fullScreenEditedText !== (promptTemplateText || '') && (
                <Tooltip title="Restore original AI prompt">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFullScreenEditedText(fullScreenOriginalText);
                      // Also clear variable values so edited state fully resets
                      if (currentPromptVariables.length && onVariableChange) {
                        currentPromptVariables.forEach((v) => {
                          if (v && v.name) onVariableChange(v.name, '');
                        });
                      }
                    }}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { 
                        color: 'warning.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Variables button - show when prompt has variables */}
              {currentPromptVariables.length > 0 && (
                <Tooltip title={`Variables (${currentPromptVariables.length})`}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVariableMenuAnchor(e.currentTarget);
                    }}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { 
                        color: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <Badge 
                      badgeContent={currentPromptVariables.length} 
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: '18px',
                          height: '18px',
                          padding: '0 4px'
                        }
                      }}
                    >
                      <CodeIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              <EditPreviewToggle
                mode={promptReadMode}
                onBeforeModeChange={async () => {
                  if (promptReadMode === 'edit') {
                    await flushPromptEditor?.();
                  }
                }}
                onModeChange={setPromptReadMode}
              />
            </Box>
            <Tooltip title="Exit full screen">
              <IconButton 
                onClick={handleCloseFullScreen}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {(() => {
            const displayText = fullScreenEditedText || fullScreenOriginalText || promptTemplateText || '';
            return (
              <Box
                sx={{
                  ...promptContentGlowStandaloneSx,
                  width: '100%',
                }}
              >
                  <CapabilityInstructionBody
                    value={displayText}
                    onChange={(text) => {
                      setFullScreenEditedText(text);
                      setLocalPrompt((prev) => ({
                        ...(prev || currentPrompt || {}),
                        variables: mergeVariablesFromTemplate(text, currentPromptVariables),
                      }));
                    }}
                    mode={promptReadMode}
                    onModeChange={setPromptReadMode}
                    onBeforePreview={flushPromptEditor}
                    keepEditorMounted
                    editorControlRef={promptEditorRef}
                    clickAwayToPreview
                    contentTextSx={instructionBodyTextSx}
                    minRows={16}
                    variables={currentPromptVariables}
                    variableValues={variableValues}
                    onVariableClick={(variable) => {
                      setEditingVariable(variable);
                    }}
                    onBlur={flushPromptEditor}
                    previewSx={{ '& .markdown-body': capabilityInstructionPreviewMarkdownSx }}
                  />
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Request Access Dialog */}
      <Dialog
        open={showRequestAccessDialog}
        onClose={handleCloseRequestAccessDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Access</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You don't have access to that knowledge. Request access from {requestAccessContext?.author?.name || requestAccessContext?.userId || 'the owner'}.
          </Typography>
          {shareableLink && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                Share this link to request access:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  mb: 1.5
                }}
              >
                {shareableLink}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<CloneIcon />}
                  onClick={handleCopyShareLink}
                  size="small"
                >
                  Copy Link
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestAccessDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Temporary context builder - session-only context for this modal */}
      <ContextBuilderDialog
        open={showTemporaryContextBuilder}
        onClose={() => {
          setShowTemporaryContextBuilder(false);
        }}
        isEditing={false}
        userProfile={temporaryContextBuilderUserProfile}
        isTemporary={true}
        onSave={async ({ contextData: savedContextData, contextPreview: savedContextPreview }) => {
          const name = (savedContextData.name || '').trim() || 'Temporary knowledge';
          const content = savedContextData.content || savedContextPreview || '';
          const id = `temp-${crypto.randomUUID()}`;
          const newTemp = { id, name, content, category: 'Temporary', hasAccess: true };
          setTemporaryContexts(prev => [...prev, newTemp]);
          setIncludedContexts(prev => ({ ...prev, [id]: true }));
          setShowTemporaryContextBuilder(false);
        }}
      />

      <ContextBuilderDialog
        open={Boolean(editingAttachedContext)}
        onClose={() => setEditingAttachedContext(null)}
        isEditing={true}
        editingContextId={editingAttachedContext?.id || null}
        initialContextData={editingAttachedContext}
        userProfile={temporaryContextBuilderUserProfile || cachedProfile?.data || null}
        onSave={handleSaveAttachedContext}
      />

      {/* Hidden PermissionSettings host for in-modal custom permissions management */}
      <Box sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
        <PermissionSettings
          permissions={permissionDraftPermissions}
          onPermissionsChange={handlePermissionSettingsChange}
          userProfile={temporaryContextBuilderUserProfile || cachedProfile?.data || { teamId: user?.teamId, companyId: user?.companyId }}
          isEditing={!!currentPrompt?.id}
          entityType="prompt"
          showCommunityOption={false}
          permissionLevels={permissionLevels}
          accessList={permissionAccessList}
          onAccessListChange={setPermissionAccessList}
          creatorId={currentPrompt?.userId || user?.id}
          entityData={currentPrompt}
          visibility={permissionDraftLevel === 'company' ? 'organization' : permissionDraftLevel}
          openAddAccessRef={openCustomPermissionRef}
          hideLegacyToggles={true}
          controlledPermissionsSync={true}
        />
      </Box>

      {/* Attach existing context selector - adds existing contexts as temporary (session-only, not saved to prompt) */}
      <ContextSelector
        open={showAttachExistingContextSelector}
        onClose={() => setShowAttachExistingContextSelector(false)}
        selectedContextIds={temporaryContexts.map((context) => context.id)}
        pinnedItems={temporaryContexts}
        onSelect={replaceTemporaryContextsFromSelection}
      />

      <Dialog
        open={showTempContextConvertDialog}
        onClose={() => {
          resetTempContextConversionState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save temporary knowledge?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which temporary knowledge should become permanent knowledge for this capability.
            Unselected knowledge will not be saved with the capability.
          </Typography>
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            {pendingTempContexts.map((ctx) => {
              const checked = selectedTempContextIdsToConvert.includes(ctx.id);
              const preview = String(ctx.content || '').trim();
              return (
                <Paper
                  key={ctx.id}
                  variant="outlined"
                  sx={{ p: 1.25, display: 'flex', gap: 1, alignItems: 'flex-start' }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={(event) => {
                      setSelectedTempContextIdsToConvert((prev) => {
                        if (event.target.checked) {
                          return prev.includes(ctx.id) ? prev : [...prev, ctx.id];
                        }
                        return prev.filter((id) => id !== ctx.id);
                      });
                    }}
                    sx={{ mt: -0.75 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {ctx.name || 'Temporary knowledge'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {preview ? preview.slice(0, 180) : 'No preview available'}
                      {preview.length > 180 ? '...' : ''}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
          <Alert severity="info">
            You can review and edit each selected knowledge item before it is saved.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetTempContextConversionState}
          >
            Cancel
          </Button>
          <Button onClick={handleSkipTempContextsAndContinue}>Save capability without temporary knowledge</Button>
          <Button
            variant="contained"
            onClick={handleStartTempContextConversion}
            disabled={selectedTempContextCount === 0}
          >
            Save selected knowledge
          </Button>
        </DialogActions>
      </Dialog>

      <ContextBuilderDialog
        key={`${activeTempContextToConvert?.id || 'convert-temp-context'}-${tempContextConversionStepKey}`}
        open={showConvertTempContextBuilder}
        onClose={() => {
          resetTempContextConversionState();
        }}
        isEditing={false}
        userProfile={temporaryContextBuilderUserProfile}
        isTemporary={false}
        closeOnSave={false}
        notice={
          activeTempContextToConvert ? (
            <Alert severity="info" sx={{ flexShrink: 0 }}>
              Saving knowledge {activeTempContextConversionIndex} of {tempContextConversionQueue.length}: {activeTempContextToConvert.name || 'Temporary knowledge'}
            </Alert>
          ) : null
        }
        secondaryAction={
          <Button onClick={handleSkipActiveTempContext}>
            Skip this knowledge
          </Button>
        }
        initialContextData={activeTempContextToConvert ? {
          id: activeTempContextToConvert.id,
          name: activeTempContextToConvert.name || 'Temporary knowledge',
          description: '',
          contextType: 'file_load',
          content: activeTempContextToConvert.content || '',
          source: 'MANUAL_ENTRY',
          sourceFileName: '',
          sourceFileType: '',
          category: 'General',
          permissions: {
            companyAccess: true,
            teamAccess: user?.teamId ? [user.teamId] : [],
            groupAccess: [],
            individualAccess: [],
            canEdit: [],
            canShare: [],
            canDelete: [],
            viewOnlyAccess: []
          }
        } : null}
        onSave={async ({ contextData: savedContextData, contextPreview: savedContextPreview }) => {
          if (!activeTempContextToConvert) return;
          const companyId = user?.companyId || temporaryContextBuilderUserProfile?.companyId;
          if (!companyId) {
            throw new Error('Unable to determine company for context conversion');
          }
          const safeName = String(savedContextData.name || activeTempContextToConvert.name || '').trim() || 'Saved knowledge';
          const createPayload = {
            name: safeName,
            description: savedContextData.description || '',
            content: savedContextData.content || savedContextPreview || '',
            source: savedContextData.source || 'MANUAL_ENTRY',
            sourceFileName: savedContextData.sourceFileName || '',
            sourceFileType: savedContextData.sourceFileType || '',
            tags: savedContextData.tags || [],
            category: savedContextData.category || 'General',
            permissions: savedContextData.permissions || {},
            companyId,
            teamId: user?.teamId || temporaryContextBuilderUserProfile?.teamId || null
          };
          const result = await createContext(createPayload);
          const created = result?.context || result?.data?.context || result;
          if (!created?.id) {
            throw new Error('Failed to create permanent context');
          }
          await completeActiveTempContextConversion({
              id: created.id,
              name: created.name || savedContextData.name,
              category: created.category || savedContextData.category || 'General'
          });
        }}
      />

      {/* Snackbar for request access notifications */}
      <Portal>
      <Snackbar
        open={requestAccessSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setRequestAccessSnackbar({ ...requestAccessSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setRequestAccessSnackbar({ ...requestAccessSnackbar, open: false })} severity={requestAccessSnackbar.severity} sx={{ width: '100%' }}>
          {requestAccessSnackbar.message}
        </Alert>
      </Snackbar>
      </Portal>
    </Dialog>
    </>
  );
};

export default PromptDialog;
